/**
 * DistillPageV2 — index.tsx  (Orchestrator)
 *
 * The root component for the V2 funnel at /distill-v2.
 * This file owns ALL state, ALL effects, and ALL API handlers.
 * It renders nothing itself — it delegates each screen to a dedicated
 * step component and passes only the props that component needs.
 *
 * Flow:
 *   Step 1 (input)    → user types a raw thought
 *   Step 2 (insight)  → distilled insight + LinkedIn post with angle picker
 *   Step 3 (template) → template picker grid
 *   Step 3.5 (content)→ content review / markdown editor + brand fields
 *   Step 4 (carousel) → PDF viewer, edit panel, LinkedIn posting
 *
 * D1 edit controls (slide count, right panel editor, undo/redo) all live
 * on Step 4 and are orchestrated from this file.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import "../../landing.css";

import { extractHashtags } from "../../utils/extractHashtags";
import { reviseApi } from "../../api/distillNode";
// Step components
import StepInput          from "./StepInput";
import StepInsight        from "./StepInsight";
import StepTemplatePicker from "./StepTemplatePicker";
import StepContent        from "./StepContent";
import StepCarousel       from "./StepCarousel";
import EditPanel          from "./EditPanel";
import LinkedInPostPreview from "../../components/LinkedInPostPreview";

// Shared types and constants
import type {
  Step, AngleKey, DistillResult, ContentSlide, CarouselStructure,
  BrandField, CarouselThemeMeta, CarouselTemplateMeta, GeneratedTemplate,
} from "./types";
import { CRAFTING_MSGS, HISTORY_MAX_DEPTH, type TemplateCategoryKey } from "./constants";
import { pageStyle, navStyle, mainStyle, ACCENT } from "./sharedStyles";

// ── Auth helpers ──────────────────────────────────────────────────────────────

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8085";

/** Reads JWT from localStorage (same strategy as V1 DistillPage).
 *  When neither token is present we log a warning — silent missing-header is the
 *  symptom that hid a backend 500 on guest-login for an entire session before. */
function authHeaders(): Record<string, string> {
  const auth  = localStorage.getItem("distill_auth_token");
  const guest = localStorage.getItem("distill_guest_token");
  const token = auth || guest;
  if (!token) {
    console.warn("[authHeaders] No token in localStorage — request will go out without Authorization header");
  }
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const axiosCfg = () => ({ withCredentials: true, headers: authHeaders() });

// D2 — maximum number of simultaneous template tabs (configurable via .env)
const MAX_TEMPLATE_TABS = parseInt(process.env.REACT_APP_MAX_TEMPLATE_TABS ?? "3", 10);

// Admin allow-list (mirrors Node/src/config/config.ts admin_emails). Keep these in sync.
// Used to gate admin-only UI such as the LinkedIn direct-publish buttons.
const ADMIN_EMAILS = [
  "admin@dkafka.com",
  "vikas@dkafka.com",
  "aditi@dkafka.com",
  "shubham@dkafka.com",
];

// ── pdfjs worker — initialised once at module load ───────────────────────────
// Both the carousel viewer and the template-preview loader need pdfjs.
// Setting GlobalWorkerOptions here ensures it's done exactly once regardless
// of which code path runs first.
let _pdfjsWorkerSet = false;
async function getPdfjsLib() {
  const pdfjsLib = await import("pdfjs-dist");
  if (!_pdfjsWorkerSet) {
    (pdfjsLib as any).GlobalWorkerOptions.workerSrc =
      `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.min.js`;
    _pdfjsWorkerSet = true;
  }
  return pdfjsLib;
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

/**
 * Returns a new slides array with brand values injected into every slide's
 * fields. Pure — no state mutation. Used just before /api/carousel/render.
 */
function withBrandValues(
  slides: ContentSlide[],
  values: Record<string, string>,
): ContentSlide[] {
  return slides.map(slide => {
    const newFields = { ...slide.fields };
    for (const [key, val] of Object.entries(values)) {
      if (key in newFields) newFields[key] = val.trim();
    }
    return { ...slide, fields: newFields };
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

const DistillPageV2: React.FC = () => {
  const location = useLocation();
  const history  = useHistory();

  // ── Draft persistence ─────────────────────────────────────────────────────
  // draftIdRef          — DB id of the in-progress draft (null = not yet created)
  // draftStateRef       — mirrors what we last wrote to the DB (for partial merges)
  // pendingRestoreTemplateRef — template to auto-pick after distillResult commits
  //   (lastStep=3, no saved slides — calls handlePickTemplate → structure → render)
  // pendingRestoreSlidesRef   — {slides, colorTheme} saved from prior session;
  //   when set, skips structure fetch → calls /render directly with saved content
  const draftIdRef                = useRef<number | null>(null);
  const draftStateRef             = useRef<Record<string, any>>({});
  const pendingRestoreTemplateRef = useRef<string | null>(null);
  const pendingRestoreSlidesRef   = useRef<{ slides: ContentSlide[]; colorTheme: string } | null>(null);

  // True while fetching a ?draft= restore — prevents any step screen from
  // flashing before we know which step to land on.
  const [isDraftLoading, setIsDraftLoading] = useState(() =>
    !!new URLSearchParams(window.location.search).get("draft")
  );

  // ── Step & core flow ──────────────────────────────────────────────────────
  const [step,    setStep]    = useState<Step>("input");
  const [thought, setThought] = useState("");
  const [preferredSlideCount, setPreferredSlideCount] = useState<number | null>(null);

  // ── Step 1 / 2 — Distill + post generation ───────────────────────────────
  const [distillResult,     setDistillResult]     = useState<DistillResult | null>(null);
  const [activeAngle,       setActiveAngle]        = useState<AngleKey>("thoughtful");
  const [perspective,       setPerspective]        = useState<"first_person" | "observer">("observer");
  const [generatedPosts,    setGeneratedPosts]     = useState<Partial<Record<AngleKey, string>>>({});
  const [linkedinPost,      setLinkedinPost]       = useState("");
  const [isEditing,              setIsEditing]              = useState(false);
  const [isRevising,             setIsRevising]             = useState(false);
  // Per-angle: the first post generated for that angle — never overwritten by revisions.
  // Sent as `originalPost` so every revision regenerates from a clean baseline.
  const [originalPosts,          setOriginalPosts]          = useState<Partial<Record<AngleKey, string>>>({});
  // Per-angle: accumulated revision instructions in the order the user gave them.
  // Every call to handleRevise appends the new instruction so the LLM always sees
  // the full correction history and can't re-introduce what was fixed before.
  const [revisionInstructions,   setRevisionInstructions]   = useState<Partial<Record<AngleKey, string[]>>>({});
  const [isDistilling,      setIsDistilling]       = useState(false);
  const [distillError,      setDistillError]       = useState<string | null>(null);
  const [isGeneratingPost,  setIsGeneratingPost]   = useState(false);

  // ── Step 3 — Template picker ──────────────────────────────────────────────
  const [templates,          setTemplates]          = useState<CarouselTemplateMeta[]>([]);
  const [templatesError,     setTemplatesError]     = useState<string | null>(null);
  const [templatesLoaded,    setTemplatesLoaded]    = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [activeCategory,     setActiveCategory]     = useState<TemplateCategoryKey>("all");

  // NOTE: V2 picker no longer fetches sample PDFs at runtime — it uses
  // pre-rendered PNG thumbnails (TEMPLATE_THUMBNAIL_IMAGES). The previous state
  // (previewPdfDocs / previewCanvasRefs / previewRenderRefs / previewLoadErrors
  // / previewInFlightRef) and the lazy-load + render effects are deleted.
  // See Node/src/scripts/generate-template-thumbnails.ts for thumbnail regen.

  // ── Step 3.5 — Content review ─────────────────────────────────────────────
  const [isStructureLoading, setIsStructureLoading] = useState(false);
  const [carouselStructure,  setCarouselStructure]  = useState<CarouselStructure | null>(null);
  const [editableSlides,     setEditableSlides]     = useState<ContentSlide[]>([]);
  // Set when /api/carousel/structure fails for a user-picked template.
  // Surfaced inline on the picker so the user can retry or choose another template.
  // We deliberately do NOT fall back to the legacy /api/carousel endpoint, which
  // would silently swap to a different template (root cause of "picked Pink, got
  // Framework Breakdown" bug — fixed both backend-side and here).
  const [pickError, setPickError] = useState<string | null>(null);

  // Brand fields (used in both Step 3.5 and the carousel edit panel)
  const [brandFields,    setBrandFields]    = useState<BrandField[]>([]);
  const [brandValues,    setBrandValues]    = useState<Record<string, string>>({});
  const [brandOriginals, setBrandOriginals] = useState<Record<string, string>>({});
  const [isSavingBrand,  setIsSavingBrand]  = useState(false);
  const [brandSaveError, setBrandSaveError] = useState<string | null>(null);
  const [brandSaved,     setBrandSaved]     = useState(false);

  // ── Step 4 — Carousel ─────────────────────────────────────────────────────
  const [isCarouselLoading,   setIsCarouselLoading]   = useState(false);
  const [carouselError,       setCarouselError]        = useState(false);
  const [craftingMessage,     setCraftingMessage]      = useState(CRAFTING_MSGS[0]);
  const [allThemePdfs,        setAllThemePdfs]         = useState<Record<string, string>>({});
  const [activeTheme,         setActiveTheme]          = useState("");
  const [approvedThemes,      setApprovedThemes]       = useState<CarouselThemeMeta[]>([]);
  const [currentSlide,        setCurrentSlide]         = useState(0);
  const [totalSlides,         setTotalSlides]          = useState(0);
  const [carouselPdfDoc,      setCarouselPdfDoc]       = useState<any>(null);
  const [carouselPptxBase64,  setCarouselPptxBase64]   = useState<string | null>(null);
  const [carouselRenderer,    setCarouselRenderer]     = useState<"html" | "pptx">("html");
  const [carouselUsageId,     setCarouselUsageId]      = useState<string | null>(null);
  const carouselCanvasRef = useRef<HTMLCanvasElement>(null);
  const carouselRenderRef = useRef<any>(null);

  // ── D1 — Edit controls (carousel step only) ───────────────────────────────
  const [selectedSlideCount, setSelectedSlideCount] = useState<number | null>(null);
  const [regenInstruction,   setRegenInstruction]   = useState("");
  const [isRestructuring,    setIsRestructuring]    = useState(false);
  const [pendingRender,      setPendingRender]      = useState(false);
  const [isRendering,        setIsRendering]        = useState(false);
  const [customBrandColor,   setCustomBrandColor]   = useState<string>('');
  const [viewMode,           setViewMode]           = useState<"single" | "grid">("grid");
  const [panelOpen,          setPanelOpen]          = useState(false);
  const [editHistory,        setEditHistory]        = useState<ContentSlide[][]>([]);
  const [historyIndex,       setHistoryIndex]       = useState(-1);

  // True while any panel input is focused — suppresses Ctrl+Z/Y
  const panelFocusRef   = useRef(false);
  // Stable refs so the keyboard listener never captures stale closures
  const handleUndoRef   = useRef<() => void>(() => {});
  const handleRedoRef   = useRef<() => void>(() => {});
  // Synced to isAuthenticated — stable in interceptor/async closures
  const isAuthenticatedRef   = useRef(false);
  // In-flight guest-token promise — awaited in handleGenerateCarousel to prevent race
  const guestTokenReadyRef   = useRef<Promise<void> | null>(null);
  // Set by state restore when PDFs were stripped; triggers auto-render after mount
  const restoreNeedsRenderRef = useRef(false);
  // Guards against React 18 StrictMode double-invocation of the auth init useEffect.
  // Without this, dev-mode would fire two parallel POST /auth/guest-login calls
  // and create two guest user rows in the DB.
  const authInitFiredRef = useRef(false);

  // ── D2 — Multi-template tabs ──────────────────────────────────────────────
  const [generatedTemplates,    setGeneratedTemplates]    = useState<GeneratedTemplate[]>([]);
  const [activeTabId,           setActiveTabId]            = useState<string>("");
  const [pickerSheetOpen,       setPickerSheetOpen]        = useState(false);
  const [sheetCategory,         setSheetCategory]          = useState<TemplateCategoryKey>("all");
  // Cached grid thumbnail data-URLs for the ACTIVE tab. Passed to PdfStrip as
  // preRenderedUrls so return visits to a tab never re-parse the PDF.
  const [carouselThumbnailUrls, setCarouselThumbnailUrls] = useState<string[]>([]);
  // Stable ref so async handleAddTemplate can check which tab is active
  // without creating stale closures over activeTabId state.
  const activeTabIdRef      = useRef<string>("");
  // When true, the PDF-parse useEffect skips re-parsing because we are
  // restoring a cached pdfjs doc during a tab switch.
  const skipNextPdfParseRef = useRef(false);

  // ── LinkedIn posting state ────────────────────────────────────────────────
  const [liPrepared,    setLiPrepared]    = useState(false);
  const [liPreviewOpen, setLiPreviewOpen] = useState(false);
  const [liLaunching,   setLiLaunching]   = useState(false);
  const [liPasteOverlay,setLiPasteOverlay]= useState(false);
  const [liFeedback,    setLiFeedback]    = useState<"up" | "down" | null>(null);
  const [liFullText,    setLiFullText]    = useState("");
  const [liShareToken,  setLiShareToken]  = useState<string | null>(null);

  // ── LinkedIn direct-publish state (admin only) ────────────────────────────
  const [liDirectConnected,  setLiDirectConnected]  = useState(false);
  const [liDirectPublishing, setLiDirectPublishing] = useState(false);
  const [liDirectError,      setLiDirectError]      = useState<string | null>(null);
  // Live LinkedIn post URL after a successful direct publish — when non-null,
  // LinkedInPasteOverlay renders in "published" mode (different copy + View-on-LinkedIn CTA).
  const [liDirectPostUrl,    setLiDirectPostUrl]    = useState<string | null>(null);

  // ── Responsive ────────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  // ── Auth ──────────────────────────────────────────────────────────────────
  const [installId,       setInstallId]       = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authUserName,    setAuthUserName]    = useState(""); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [isPro,           setIsPro]           = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [isAdminUser,     setIsAdminUser]     = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [showAuthModal,   setShowAuthModal]   = useState(false);
  const [loggedOutToast,  setLoggedOutToast]  = useState(false);
  // "none" → no prompt  |  "soft" → dismissible (1st publish)  |  "hard" → blocking (2nd+ publish)
  const [signInPromptLevel, setSignInPromptLevel] = useState<"none" | "soft" | "hard">("none");
  type AuthModalReason = "nav" | "download" | "copy" | "publish" | "session";
  const [authModalReason, setAuthModalReason] = useState<AuthModalReason>("nav");
  const openAuthModal = (reason: AuthModalReason) => { setAuthModalReason(reason); setShowAuthModal(true); };
  const AUTH_MODAL_COPY: Record<AuthModalReason, { title: string; sub: string }> = {
    nav:      { title: "Sign in to Distill",     sub: "Save your insights, sync across devices, and unlock all features." },
    download: { title: "Sign in to download",    sub: "Create a free account to download your carousel as PDF or PowerPoint." },
    copy:     { title: "Sign in to copy",        sub: "Create a free account to copy and use your content." },
    publish:  { title: "Sign in to publish",     sub: "Create a free account to post your carousel to LinkedIn." },
    session:  { title: "Session expired",        sub: "Your session has expired. Sign in again — your work is saved and you'll return right where you left off." },
  };

  // ── Session expiry ────────────────────────────────────────────────────────
  const [sessionExpired, setSessionExpired] = useState(false);

  // ── Derived values ────────────────────────────────────────────────────────
  const slideCountMin       = carouselStructure?.slideCountMin ?? 1;
  const slideCountMax       = carouselStructure?.slideCountMax ?? 10;
  const canUndo             = historyIndex > 0;
  const canRedo             = historyIndex < editHistory.length - 1;
  const hasPanelBrandChanges = brandFields.some(
    f => (brandValues[f.key] ?? "") !== (brandOriginals[f.key] ?? "")
  );

  // ── Effects ───────────────────────────────────────────────────────────────

  // Catch 401s globally — but ONLY show "Session expired" if the user held a real JWT.
  // Anonymous/guest 401s (e.g. race condition before guest token arrives) propagate
  // to each call-site's catch block so the carousel shows its own error state instead
  // of the misleading "Session expired" overlay.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      r => r,
      err => {
        if (err?.response?.status === 401) {
          localStorage.removeItem("distill_auth_token");
          if (isAuthenticatedRef.current) {
            isAuthenticatedRef.current = false;
            setIsAuthenticated(false);
            setSessionExpired(true);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // ── Auth initialisation + guest token ────────────────────────────────────
  // Mirrors V1 DistillPage: sets installId, checks JWT, auto-issues guest token.
  // Also restores state saved by saveV2State() before an OAuth redirect so the
  // user lands back exactly where they left off after signing in.
  useEffect(() => {
    // StrictMode guard: dev-mode invokes effects twice to surface bugs. Without
    // this we'd POST /auth/guest-login twice on mount, creating two guest rows.
    if (authInitFiredRef.current) return;
    authInitFiredRef.current = true;

    // ── Restore state saved before OAuth redirect ─────────────────────────
    // Check first so the user sees their carousel immediately on return,
    // not a blank Step 1.
    const savedRaw = sessionStorage.getItem("distill_v2_state");
    if (savedRaw) {
      try {
        const s = JSON.parse(savedRaw);
        sessionStorage.removeItem("distill_v2_state");
        // Only restore if saved within the last 10 minutes
        if (Date.now() - s.ts < 10 * 60 * 1000) {
          if (s.step)                          setStep(s.step);
          if (s.thought)                       setThought(s.thought);
          if (s.distillResult)                 setDistillResult(s.distillResult);
          if (s.activeAngle)                   setActiveAngle(s.activeAngle === "personal" ? "thoughtful" : s.activeAngle);
          if (s.generatedPosts)                setGeneratedPosts(s.generatedPosts);
          if (s.linkedinPost)                  setLinkedinPost(s.linkedinPost);
          if (s.isEditing != null)             setIsEditing(s.isEditing);
          if (s.preferredSlideCount != null)   setPreferredSlideCount(s.preferredSlideCount);
          if (s.selectedTemplateId)            setSelectedTemplateId(s.selectedTemplateId);
          if (s.activeCategory)                setActiveCategory(s.activeCategory);
          if (s.carouselStructure)             setCarouselStructure(s.carouselStructure);
          if (s.editableSlides?.length)        setEditableSlides(s.editableSlides);
          if (s.brandFields?.length)           setBrandFields(s.brandFields);
          if (s.brandValues)                   setBrandValues(s.brandValues);
          if (s.brandOriginals)                setBrandOriginals(s.brandOriginals);
          if (s.allThemePdfs && Object.keys(s.allThemePdfs).length) setAllThemePdfs(s.allThemePdfs);
          if (s.activeTheme)                   setActiveTheme(s.activeTheme);
          if (s.approvedThemes?.length)        setApprovedThemes(s.approvedThemes);
          if (s.currentSlide != null)          setCurrentSlide(s.currentSlide);
          if (s.totalSlides != null)           setTotalSlides(s.totalSlides);
          if (s.carouselPptxBase64)            setCarouselPptxBase64(s.carouselPptxBase64);
          if (s.carouselRenderer)              setCarouselRenderer(s.carouselRenderer);
          if (s.carouselUsageId)               setCarouselUsageId(s.carouselUsageId);
          if (s.selectedSlideCount != null)    setSelectedSlideCount(s.selectedSlideCount);
          if (s.regenInstruction != null)      setRegenInstruction(s.regenInstruction);
          if (s.viewMode)                      setViewMode(s.viewMode);
          if (s.panelOpen != null)             setPanelOpen(s.panelOpen);
          if (s.editHistory?.length)           setEditHistory(s.editHistory);
          if (s.historyIndex != null)          setHistoryIndex(s.historyIndex);
          if (s.generatedTemplates?.length) {
            // pdfDoc is null in the snapshot — PDF parse useEffect re-parses from allThemePdfs
            setGeneratedTemplates(s.generatedTemplates);
            const tabId = s.activeTabId ?? "";
            setActiveTabId(tabId);
            activeTabIdRef.current = tabId;
            // Defensive: existing sessionStorage from before the activeTab/
            // selectedTemplateId sync fix may have a stale selectedTemplateId
            // pointing at the FIRST tab (handleAddTemplate didn't update it).
            // Realign to the active tab so the auto-render effect below renders
            // the right template — otherwise the backend mismatch guard
            // returns 400 and the screen goes blank.
            if (tabId) setSelectedTemplateId(tabId);
          }
          if (s.liPrepared != null)            setLiPrepared(s.liPrepared);
          if (s.liFullText)                    setLiFullText(s.liFullText);
          // Flag carousel re-render needed (PDFs were stripped from snapshot to fit sessionStorage)
          if (s.needsRender)                   restoreNeedsRenderRef.current = true;
        }
      } catch {
        sessionStorage.removeItem("distill_v2_state");
      }
    }

    // install_id — stable UUID per browser profile
    let id = localStorage.getItem("distill_install_id");
    if (!id) {
      id = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("distill_install_id", id);
    }
    setInstallId(id);

    // Check for a real (Google-issued) JWT
    const stored = localStorage.getItem("distill_auth_token");
    if (stored) {
      try {
        const payload = JSON.parse(atob(stored.split(".")[1]));
        if (payload.exp > Date.now() / 1000) {
          setIsAuthenticated(true);
          isAuthenticatedRef.current = true;   // keep interceptor ref in sync
          if (payload.name || payload.email) setAuthUserName(payload.name || payload.email);
          if (payload.role === "admin" || ADMIN_EMAILS.includes((payload.email || "").toLowerCase())) setIsAdminUser(true);
          // Fetch isPro from profile (non-blocking)
          axios.get(`${API_URL}/api/user/profile`, {
            headers: { Authorization: `Bearer ${stored}` },
            withCredentials: true,
          }).then(r => {
            if (r.data?.isPro) setIsPro(true);
            if (r.data?.role === "admin" || ADMIN_EMAILS.includes((r.data?.email || "").toLowerCase())) setIsAdminUser(true);
          }).catch(() => {});
        } else {
          localStorage.removeItem("distill_auth_token");
        }
      } catch {
        localStorage.removeItem("distill_auth_token");
      }
    } else {
      // No real JWT — ensure a guest JWT exists so carousel generation works for anonymous
      // users without any sign-in prompt. Store the in-flight promise so handleGenerateCarousel
      // can await it and avoid a race condition when the user picks a template immediately.
      const existingGuest = localStorage.getItem("distill_guest_token");
      const guestValid = existingGuest ? (() => {
        try {
          const p = JSON.parse(atob(existingGuest.split(".")[1]));
          return p.exp > Date.now() / 1000;
        } catch { return false; }
      })() : false;

      if (!guestValid) {
        // Strip any stale guest token first — if guest-login fails (network/500/no-jwt
        // response), we must NOT leave an old token lying around for authHeaders() to send.
        localStorage.removeItem("distill_guest_token");
        guestTokenReadyRef.current = axios.post(`${API_URL}/auth/guest-login`, { install_id: id })
          .then(r => {
            if (r.data?.jwt) {
              localStorage.setItem("distill_guest_token", r.data.jwt);
              console.log("[auth-init] guest-login OK, jwt stored (length:", r.data.jwt.length, ")");
            } else {
              console.error("[auth-init] guest-login returned no jwt — response keys:", Object.keys(r.data ?? {}));
            }
          })
          .catch(e => {
            // Surface the failure instead of swallowing — the user will see carouselError later
            // when they pick a template, but at least the cause is in the console.
            console.error("[auth-init] guest-login FAILED:", e?.response?.status, e?.response?.data, e?.message);
          });
      }

      // Show post-publish sign-in prompt if user has published before but isn't signed in
      const publishCount = parseInt(localStorage.getItem("distill_publish_count") ?? "0", 10);
      if (publishCount >= 2)      setSignInPromptLevel("hard");
      else if (publishCount >= 1) setSignInPromptLevel("soft");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ?q param auto-submit (from Landing Page "Try It Out") ────────────────
  // Fires exactly once after installId is set (same guard as V1 DistillPage).
  useEffect(() => {
    if (!installId) return;
    const q = new URLSearchParams(location.search).get("q");
    if (q) {
      const text = q.trim();
      setThought(text);
      handleDistill(text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installId]);

  // ── ?draft=ID — restore a saved draft ────────────────────────────────────
  // Fires once after installId is available (so axiosCfg has a valid token).
  // isDraftLoading is pre-set to true (synchronously from URL) so no step
  // screen flashes before we know where to land.
  //
  // Landing logic:
  //   lastStep=1 (thought only, never distilled)  → input page, thought pre-filled
  //   lastStep=2 (distilled + post, no template)  → insight/post page
  //   lastStep=3 (template chosen, slides saved)   → summoning animation → carousel
  //   lastStep=3 (template chosen, no saved slides)→ summoning animation → re-generate
  useEffect(() => {
    if (!installId) return;
    const draftParam = new URLSearchParams(location.search).get("draft");
    if (!draftParam) { setIsDraftLoading(false); return; }

    axios.get(`${API_URL}/api/drafts/${draftParam}`, axiosCfg())
      .then(r => {
        const s        = r.data?.state ?? {};
        const lastStep = r.data.lastStep as number;
        draftIdRef.current    = r.data.id;
        draftStateRef.current = s;

        // Always restore raw thought so user can re-distill if they want
        if (s.rawThought) setThought(s.rawThought);

        if (!s.distilledInsight) {
          // Only a thought was saved — show input page with it pre-filled
          setIsDraftLoading(false);
          // step stays "input" ✓

        } else {
          // Restore the distill result no matter which step we land on
          setDistillResult({
            distilledInsight: s.distilledInsight,
            topic:    s.topic    ?? "",
            hook:     s.hook     ?? "",
            toneTags: s.toneTags ?? [],
            pov:      s.pov      ?? { agree: "", disagree: "" },
          } as DistillResult);
          if (s.angle)        setActiveAngle((s.angle === "personal" ? "thoughtful" : s.angle) as AngleKey);
          if (s.linkedinPost) setLinkedinPost(s.linkedinPost);

          if (lastStep >= 3 && s.selectedTemplateId) {
            // Template was already chosen — aim for the carousel
            setSelectedTemplateId(s.selectedTemplateId);
            if (s.colorTheme) setActiveTheme(s.colorTheme);
            pendingRestoreTemplateRef.current = s.selectedTemplateId;

            if (Array.isArray(s.editableSlides) && s.editableSlides.length > 0) {
              // We have the exact slide content from the prior session — no Gemini re-call
              pendingRestoreSlidesRef.current = {
                slides:     s.editableSlides,
                colorTheme: s.colorTheme ?? '',
              };
            }
            // isDraftLoading stays true; carousel animation shows once watcher fires

          } else {
            // Distilled but no template yet — land directly on the post page
            setStep("insight");
            setIsDraftLoading(false);
          }
        }

        // Clean the URL so a hard-refresh doesn't re-trigger the restore
        history.replace({ pathname: location.pathname, search: "" });
      })
      .catch(() => {
        setIsDraftLoading(false);
        history.replace({ pathname: location.pathname, search: "" });
      });
  }, [installId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Draft helpers (fire-and-forget, never block UI) ───────────────────────
  // saveDraft: merges partialState into the ref, then creates or patches the DB row.
  // deleteDraft: removes the draft on publish / "start over".
  const saveDraft = useCallback((partialState: Record<string, any>, lastStep: number) => {
    const merged = { ...draftStateRef.current, ...partialState };
    draftStateRef.current = merged;
    const installId = localStorage.getItem("distill_install_id") ?? undefined;

    if (draftIdRef.current == null) {
      axios.post(`${API_URL}/api/drafts`, {
        state: merged, last_step: lastStep, install_id: installId,
      }, axiosCfg())
        .then(r => { draftIdRef.current = r.data.id; })
        .catch(() => {});
    } else {
      axios.patch(`${API_URL}/api/drafts/${draftIdRef.current}`, {
        state: merged, last_step: lastStep,
      }, axiosCfg()).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteDraft = useCallback(() => {
    if (draftIdRef.current == null) return;
    axios.delete(`${API_URL}/api/drafts/${draftIdRef.current}`, axiosCfg()).catch(() => {});
    draftIdRef.current    = null;
    draftStateRef.current = {};
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load all templates in background so the picker is instant when the user arrives
  useEffect(() => {
    axios.get(`${API_URL}/api/carousel/templates`, axiosCfg())
      .then(r => {
        const list = r.data?.templates ?? r.data ?? [];
        setTemplates(Array.isArray(list) ? list : []);
        setTemplatesLoaded(true);
      })
      .catch(e => {
        setTemplatesError(
          e?.response?.status === 401
            ? "Session expired — please log in again."
            : e?.response?.data?.error ?? "Failed to load templates. Please refresh."
        );
        setTemplatesLoaded(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update isMobile on window resize
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Keep selectedSlideCount in sync with the actual slide count whenever
  // editableSlides changes (remove slide, text-view save, etc.). This ensures
  // the pill selector always highlights the current count.
  useEffect(() => {
    if (editableSlides.length > 0) {
      setSelectedSlideCount(editableSlides.length);
    }
  }, [editableSlides.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-skip the brand step when all required fields are already in the user's profile.
  // This fires after all state from the structure response is committed to React, so
  // handleGenerateCarousel reads the correct carouselStructure / editableSlides values.
  useEffect(() => {
    if (step === "content" && !brandFields.some(f => f.isMissing)) {
      handleGenerateCarousel();
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Draft restore — fires once distillResult commits to state (lastStep >= 3 path).
  //
  // Branch A — saved slides exist:
  //   Set a minimal carouselStructure (themeId only) so handleGenerateCarousel
  //   takes the /render path (not legacy /carousel). Pass saved slides as
  //   slidesOverride to bypass stale-closure issues entirely. No Gemini re-call.
  //   Result: same slide content, freshly rendered PDF, animation plays.
  //
  // Branch B — no saved slides:
  //   Fall back to handlePickTemplate → structure fetch (may call Gemini) → carousel.
  useEffect(() => {
    if (!distillResult || !pendingRestoreTemplateRef.current) return;
    const tplId       = pendingRestoreTemplateRef.current;
    const savedSlides = pendingRestoreSlidesRef.current;
    pendingRestoreTemplateRef.current = null;
    pendingRestoreSlidesRef.current   = null;

    if (savedSlides && savedSlides.slides.length > 0) {
      // Branch A — direct render from saved slide content (no Gemini structure call)
      const { slides, colorTheme } = savedSlides;

      // Provide a minimal structure so handleGenerateCarousel routes to /render
      setCarouselStructure({
        slides,
        themeId:       colorTheme,
        brandFields:   [],
        slideCountMin: 1,
        slideCountMax: slides.length + 4,
      } as any);
      setBrandFields([]);
      setSelectedSlideCount(slides.length);

      setIsDraftLoading(false);

      // Call directly with slidesOverride — avoids stale-closure timing entirely.
      // handleGenerateCarousel sets isCarouselLoading=true + step="carousel" itself,
      // so the summoning animation plays without any extra state changes here.
      handleGenerateCarousel(tplId, colorTheme, slides);

    } else {
      // Branch B — no saved slides: regenerate structure (may call Gemini for content)
      setIsDraftLoading(false);
      handlePickTemplate(tplId);
    }
  }, [distillResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rotate the crafting message while the initial carousel is generating
  useEffect(() => {
    if (!isCarouselLoading) return;
    setCraftingMessage(CRAFTING_MSGS[0]);
    let idx = 0;
    const id = setInterval(() => {
      idx = (idx + 1) % CRAFTING_MSGS.length;
      setCraftingMessage(CRAFTING_MSGS[idx]);
    }, 2400);
    return () => clearInterval(id);
  }, [isCarouselLoading]);

  // Parse the base64 PDF via pdfjs-dist whenever the active theme changes.
  // Also clears isCarouselLoading / isRendering once the document is ready.
  // Skip: when skipNextPdfParseRef is true we are restoring a cached pdfjs doc
  // from a tab switch — the canvas-render useEffect will redraw from it immediately.
  useEffect(() => {
    if (skipNextPdfParseRef.current) {
      skipNextPdfParseRef.current = false;
      return;
    }
    const b64 = allThemePdfs[activeTheme];
    if (!b64) return;
    let cancelled = false;
    (async () => {
      try {
        const pdfjsLib = await getPdfjsLib();
        const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
        const doc   = await (pdfjsLib as any).getDocument({ data: bytes }).promise;
        if (!cancelled) {
          setCarouselPdfDoc(doc);
          setTotalSlides(doc.numPages);
          setCurrentSlide(0);
          setIsCarouselLoading(false);
          setIsRendering(false);
        }
      } catch {
        if (!cancelled) {
          setIsCarouselLoading(false);
          setIsRendering(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [allThemePdfs, activeTheme]);

  // Render one PDF page to the canvas whenever the slide index or doc changes.
  // The canvas is always in the DOM when showViewer is true (hidden via visibility+height
  // in grid mode), so carouselCanvasRef.current is always set and this effect can render
  // into it as soon as carouselPdfDoc first becomes available — no blank on first switch
  // to single view.
  useEffect(() => {
    if (!carouselPdfDoc || !carouselCanvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        if (carouselRenderRef.current) {
          carouselRenderRef.current.cancel();
          carouselRenderRef.current = null;
        }
        const page  = await carouselPdfDoc.getPage(currentSlide + 1);
        if (cancelled) return;
        const canvas = carouselCanvasRef.current!;
        const contW  = canvas.parentElement?.clientWidth ?? 400;
        const dpr    = window.devicePixelRatio || 1;
        const baseVp = page.getViewport({ scale: 1 });
        const scale  = (contW / baseVp.width) * dpr;
        const vp     = page.getViewport({ scale });
        canvas.width  = vp.width;
        canvas.height = vp.height;
        const task = page.render({ canvasContext: canvas.getContext("2d")!, viewport: vp });
        carouselRenderRef.current = task;
        await task.promise;
      } catch (e: any) {
        if (e?.name !== "RenderingCancelledException") console.error("[carousel PDF]", e);
      }
    })();
    return () => { cancelled = true; };
  }, [carouselPdfDoc, currentSlide]);

  // (Removed) Picker thumbnails are now pre-rendered PNGs served as <img>.
  // No runtime PDF parsing / canvas rendering on Step 3 anymore.

  // Keep keyboard-shortcut refs pointing at the latest handler closures.
  // Intentionally has no deps array: runs every render so the Ctrl+Z/Y
  // listener (which only subscribes once per [step] change) is never stale.
  useEffect(() => {
    handleUndoRef.current = handleUndo;
    handleRedoRef.current = handleRedo;
  });

  // D2 — Keep the active tab's pdfDoc cache in sync with the live carouselPdfDoc.
  // The PDF-parse useEffect sets carouselPdfDoc asynchronously after a render completes.
  // Without this effect, generatedTemplates[activeTabId].pdfDoc would stay null until
  // the user switches away (which triggers a snapshot in switchTab). That null cache
  // would cause switchTab to allow re-parsing on the NEXT return visit, which is fine —
  // but more importantly it would mean switchTab never sets skipNextPdfParseRef=true,
  // letting awaitingPdfParse stay true indefinitely when carouselPdfDoc gets cleared on
  // theme-change or render. Writing through here ensures the cache is always fresh.
  useEffect(() => {
    if (!carouselPdfDoc || !activeTabId) return;
    setGeneratedTemplates(prev => prev.map(t =>
      t.templateId === activeTabId
        ? { ...t, pdfDoc: carouselPdfDoc, totalSlides }
        : t
    ));
  }, [carouselPdfDoc, activeTabId, totalSlides]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ctrl+Z / Ctrl+Y keyboard shortcuts (carousel step only)
  useEffect(() => {
    if (step !== "carousel") return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (panelFocusRef.current) return; // don't fire while typing in panel inputs
      if (e.key === "z" && !e.shiftKey) { e.preventDefault(); handleUndoRef.current(); }
      if (e.key === "y")               { e.preventDefault(); handleRedoRef.current(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  // ── Handlers — Step 1/2 ───────────────────────────────────────────────────

  /** POST /api/distill → POST /api/generate → advance to Step 2. */
  const handleDistill = async (thoughtOverride?: string) => {
    // Guard: onClick passes a SyntheticEvent if wired directly to a button — only accept genuine strings
    const isStringOverride = typeof thoughtOverride === "string";
    const rawThought = isStringOverride ? thoughtOverride : thought;
    if (!rawThought.trim() || isDistilling) return;
    setIsDistilling(true);
    setDistillError(null);
    // Sync thought state only when a real string override is provided (re-distill from Step 2)
    if (isStringOverride) setThought(thoughtOverride);
    try {
      const distillRes = await axios.post(
        `${API_URL}/api/distill`,
        { rawThought },
        axiosCfg(),
      );
      const distill = distillRes.data as DistillResult;
      setDistillResult(distill);

      // Auto-select the angle the model suggests (falls back to "thoughtful").
      // For first_person content this is typically "personal", catching the common
      // case where the user's story would otherwise be generated as an observer take.
      const detectedPerspective = distill.perspective ?? "observer";
      const initialAngle: AngleKey = distill.suggestedAngle ?? "thoughtful";
      setPerspective(detectedPerspective);

      const genRes = await axios.post(
        `${API_URL}/api/generate`,
        {
          distilledInsight: distill.distilledInsight,
          topic:            distill.topic,
          angle:            initialAngle,
          perspective:      detectedPerspective,
          userHashtags:     extractHashtags(rawThought),
          rawThought,
        },
        axiosCfg(),
      );
      const post = (genRes.data?.outputs as any)?.linkedin ?? "";
      setActiveAngle(initialAngle);
      setGeneratedPosts({ [initialAngle]: post } as Partial<Record<AngleKey, string>>);
      setOriginalPosts({ [initialAngle]: post } as Partial<Record<AngleKey, string>>);
      setLinkedinPost(post);
      setIsEditing(false);
      setStep("insight");

      // Auto-save draft — fire-and-forget, never blocks the UI
      saveDraft({
        rawThought,
        distilledInsight: distill.distilledInsight,
        topic:            distill.topic,
        hook:             distill.hook,
        toneTags:         distill.toneTags,
        pov:              distill.pov,
        angle:            initialAngle,
        linkedinPost:     post,
      }, 1);
    } catch (e: any) {
      const serverMsg = e?.response?.data?.error;
      setDistillError(
        typeof serverMsg === "string" ? serverMsg : "Something went wrong. Please try again."
      );
    } finally {
      setIsDistilling(false);
    }
  };

  /**
   * Switch to a different tone angle.
   * Uses a cached post if the user has already visited that angle;
   * otherwise calls POST /api/generate.
   */
  const handleAngle = async (angle: AngleKey) => {
    if (!distillResult || angle === activeAngle || isGeneratingPost) return;
    setActiveAngle(angle);
    if (generatedPosts[angle]) {
      setLinkedinPost(generatedPosts[angle]!);
      return;
    }
    setIsGeneratingPost(true);
    try {
      const res = await axios.post(`${API_URL}/api/generate`, {
        distilledInsight: distillResult.distilledInsight,
        topic:            distillResult.topic,
        hook:             distillResult.hook,
        angle,
        perspective,
        userHashtags:     extractHashtags((distillResult as any).rawThought ?? thought),
        // RI-012 fix: pass the user's raw thought so Gemini can preserve specifics,
        // named entities, emotional arc, and first-person voice that distillation strips.
        // Falls back to local `thought` state if the distill response didn't echo rawThought back.
        rawThought:       (distillResult as any).rawThought ?? thought,
      }, axiosCfg());
      const post = (res.data?.outputs as any)?.linkedin ?? "";
      setGeneratedPosts(prev => ({ ...prev, [angle]: post }));
      // Anchor for revision history — never overwritten by revisions, only by re-generation
      setOriginalPosts(prev => ({ ...prev, [angle]: post }));
      // Fresh angle = fresh revision history
      setRevisionInstructions(prev => ({ ...prev, [angle]: [] }));
      setLinkedinPost(post);
      saveDraft({ angle, linkedinPost: post }, 2);
    } catch {}
    finally { setIsGeneratingPost(false); }
  };

  /**
   * Wrapper passed to StepInsight instead of the raw setLinkedinPost setter.
   * Keeps generatedPosts[activeAngle] in sync so switching angle → back does
   * NOT clobber an edit the user made inline.
   */
  const handleEditLinkedinPost = (v: string) => {
    setLinkedinPost(v);
    setGeneratedPosts(prev => ({ ...prev, [activeAngle]: v }));
  };

  /**
   * Guided revision — sends the ORIGINAL post + ALL accumulated instructions so
   * the LLM regenerates from a clean baseline with full correction context.
   * This prevents "correction amnesia" where a previous fix is silently undone
   * on the next turn because the LLM only saw the latest instruction.
   */
  const handleRevise = async (instruction: string) => {
    if (!distillResult || !linkedinPost.trim() || !instruction.trim()) return;
    setIsRevising(true);
    const previousInstructions = revisionInstructions[activeAngle] ?? [];
    const originalPost         = originalPosts[activeAngle] ?? linkedinPost;
    try {
      const res = await reviseApi({
        currentOutput:        linkedinPost,
        originalPost,
        previousInstructions,
        format:               "linkedin",
        angle:                activeAngle,
        distilledInsight:     distillResult.distilledInsight,
        topic:                distillResult.topic,
        instruction,
      });
      const revised = res.output ?? "";
      if (revised) {
        setLinkedinPost(revised);
        setGeneratedPosts(prev => ({ ...prev, [activeAngle]: revised }));
        // Append to this angle's correction history so every future call
        // carries the full list and can't re-introduce what was fixed
        setRevisionInstructions(prev => ({
          ...prev,
          [activeAngle]: [...(prev[activeAngle] ?? []), instruction.trim()],
        }));
        saveDraft({ linkedinPost: revised }, 2);
      }
    } catch {}
    finally { setIsRevising(false); }
  };

  /** Remove a previously applied instruction from the history for the active angle. */
  const handleRemoveRevisionInstruction = (index: number) => {
    setRevisionInstructions(prev => {
      const existing = prev[activeAngle] ?? [];
      return { ...prev, [activeAngle]: existing.filter((_, i) => i !== index) };
    });
  };

  /**
   * Toggle between first_person and observer perspective for the current angle.
   * Clears the cached post for the active angle so handleAngle re-generates with
   * the new perspective. Revision history is also cleared since it was written for
   * the previous perspective — keeping it would produce contradictory corrections.
   */
  const handleTogglePerspective = () => {
    const next: "first_person" | "observer" =
      perspective === "first_person" ? "observer" : "first_person";
    setPerspective(next);
    // Bust the cache for the active angle so the next render triggers a fresh generate
    setGeneratedPosts(prev => {
      const updated = { ...prev };
      delete updated[activeAngle];
      return updated;
    });
    setOriginalPosts(prev => {
      const updated = { ...prev };
      delete updated[activeAngle];
      return updated;
    });
    setRevisionInstructions(prev => ({ ...prev, [activeAngle]: [] }));
    // Trigger re-generation with the new perspective
    if (distillResult) {
      setIsGeneratingPost(true);
      axios.post(`${API_URL}/api/generate`, {
        distilledInsight: distillResult.distilledInsight,
        topic:            distillResult.topic,
        hook:             distillResult.hook,
        angle:            activeAngle,
        perspective:      next,
        userHashtags:     extractHashtags((distillResult as any).rawThought ?? thought),
        rawThought:       (distillResult as any).rawThought ?? thought,
      }, axiosCfg())
        .then(res => {
          const post = (res.data?.outputs as any)?.linkedin ?? "";
          setGeneratedPosts(prev => ({ ...prev, [activeAngle]: post }));
          setOriginalPosts(prev => ({ ...prev, [activeAngle]: post }));
          setLinkedinPost(post);
        })
        .catch(() => {})
        .finally(() => setIsGeneratingPost(false));
    }
  };

  // ── Handlers — Step 3 ─────────────────────────────────────────────────────

  // (Removed) `loadPreviewPdf` — picker thumbnails are now static PNGs served
  // as <img>, no lazy PDF.js fetch needed. See generate-template-thumbnails.ts.

  /** Builds the body for /api/carousel/structure fresh-generation calls. */
  const structureBody = (templateId: string, colorTheme: string) => {
    const tpl = templates.find(t => t.id === templateId);
    const targetCount =
      preferredSlideCount != null &&
      tpl != null &&
      preferredSlideCount >= tpl.slideCountMin &&
      preferredSlideCount <= tpl.slideCountMax
        ? preferredSlideCount
        : undefined;
    return {
      distilledInsight: distillResult!.distilledInsight,
      hook:             distillResult!.hook,
      topic:            distillResult!.topic,
      angle:            activeAngle,
      colorTheme,
      template:         templateId,
      editedCaption:    linkedinPost.trim() || undefined,
      targetSlideCount: targetCount,
    };
  };

  /**
   * User clicks a template card.
   * Immediately calls /api/carousel/structure (Gemini-only, no render) and
   * advances to the content review step.
   */
  const handlePickTemplate = async (templateId: string) => {
    if (!distillResult || isStructureLoading) return;
    setSelectedTemplateId(templateId);
    setPickError(null);
    setIsStructureLoading(true);

    // Guarantee a carousel-capable JWT exists before hitting the authenticated endpoint.
    // Guest-login fires async on mount; a fast user can click a template before it resolves.
    // Also covers the post-logout state where both tokens are absent after stale-token cleanup.
    if (!localStorage.getItem("distill_auth_token") && !localStorage.getItem("distill_guest_token")) {
      await guestTokenReadyRef.current;
    }

    const tpl          = templates.find(t => t.id === templateId);
    const defaultTheme = tpl?.approvedThemes?.[0]?.key ?? "colorshift-default";
    try {
      const res = await axios.post(`${API_URL}/api/carousel/structure`, structureBody(templateId, defaultTheme), axiosCfg());
      const structure    = res.data as CarouselStructure;
      const slidesCopy   = JSON.parse(JSON.stringify(structure.slides)) as ContentSlide[];
      setCarouselStructure(structure);
      setEditableSlides(slidesCopy);
      setSelectedSlideCount(slidesCopy.length);
      // Initialise brand panel from profile values
      const fields = structure.brandFields ?? [];
      setBrandFields(fields);
      const initValues: Record<string, string> = {};
      for (const f of fields) initValues[f.key] = f.currentValue;
      setBrandValues(initValues);
      setBrandOriginals(initValues);
      setBrandSaveError(null);
      setBrandSaved(false);
      setStep("content");
      // Save template choice + generated slide content so restore skips Gemini re-call
      saveDraft({
        selectedTemplateId: templateId,
        colorTheme:         defaultTheme,
        editableSlides:     slidesCopy,   // ContentSlide[] — the exact slide content
      }, 3);
    } catch (e: any) {
      console.error("[structure]", e);
      // 401 = session expired — show sign-in modal, do NOT blame the template.
      if (handleAuthExpiry(e)) return;
      // Surface the failure on the picker — DO NOT silently fall back to the
      // legacy /api/carousel endpoint, which would re-run the template selector
      // and pick a different template than the user chose. The user keeps their
      // pick visible and can retry the same template or choose another.
      const tplName = tpl?.name ?? templateId;
      setPickError(`Couldn't generate "${tplName}". The content didn't fit this template — try another, or click the same one again to retry.`);
      setSelectedTemplateId(null);
    } finally {
      setIsStructureLoading(false);
    }
  };

  /** Retry loading the templates list after an error. */
  const handleRetryTemplates = () => {
    setTemplatesError(null);
    setTemplatesLoaded(false);
    axios.get(`${API_URL}/api/carousel/templates`, axiosCfg())
      .then(r => { setTemplates(r.data?.templates ?? r.data ?? []); setTemplatesLoaded(true); })
      .catch(e => {
        setTemplatesError(e?.response?.data?.error ?? "Still failing — please refresh.");
        setTemplatesLoaded(true);
      });
  };

  /**
   * Save brand field values (handle, tagline, etc.) to the user's profile.
   * silent=true → fire-and-forget with no UI feedback (used during carousel generation).
   */
  const handleSaveBrandData = async (silent = false) => {
    setBrandSaveError(null);
    setBrandSaved(false);
    if (!silent) setIsSavingBrand(true);
    try {
      await axios.patch(
        `${API_URL}/api/user/brand`,
        {
          linkedinHandle: brandValues.handle  ?? undefined,
          brandTagline:   brandValues.tagline ?? undefined,
        },
        axiosCfg(),
      );
      setBrandOriginals({ ...brandValues });
      if (!silent) setBrandSaved(true);
    } catch {
      if (!silent) setBrandSaveError("Couldn't save to profile. Your values will still be used for this carousel.");
    } finally {
      if (!silent) setIsSavingBrand(false);
    }
  };

  /**
   * Step 3.5 → Step 4: render the PDF from the (possibly edited) slide content.
   * Also called as a fallback from handlePickTemplate when /structure fails.
   *
   * slidesOverride — when provided (draft restore path), bypasses editableSlides state
   *   entirely so stale-closure timing is never an issue. The override is used as-is
   *   for the /render payload; brand-value flushing is skipped (saved slides already
   *   have them baked in from the original session).
   */
  const handleGenerateCarousel = async (templateId?: string, themeOverride?: string, slidesOverride?: ContentSlide[]) => {
    const tplId     = templateId ?? selectedTemplateId;
    const structure = carouselStructure;
    if (!tplId || isCarouselLoading) return;

    // Await guest token if neither a real JWT nor a guest JWT is ready yet.
    // Prevents a 401 when the user picks a template faster than the guest-login call resolves.
    if (!localStorage.getItem("distill_auth_token") && !localStorage.getItem("distill_guest_token")) {
      await guestTokenReadyRef.current;
    }

    if (slidesOverride) {
      // Draft-restore path: slides came directly from saved state — skip brand flush
      setEditableSlides(slidesOverride);
    } else {
      // Normal path: flush any brand-panel edits into the slides before render
      setEditableSlides(prev => withBrandValues(prev, brandValues));
      // Silently persist any newly-entered brand values to the profile
      if (brandFields.some(f => f.isMissing && brandValues[f.key]?.trim())) {
        handleSaveBrandData(true);
      }
    }

    setIsCarouselLoading(true);
    setStep("carousel");

    const tpl          = templates.find(t => t.id === tplId);
    const defaultTheme = themeOverride ?? tpl?.approvedThemes?.[0]?.key ?? "colorshift-default";
    const themeId      = structure?.themeId ?? defaultTheme;

    setCarouselError(false);
    try {
      // slidesOverride (draft restore) bypasses stale-closure issue entirely.
      // Otherwise editableSlides is the source of truth (post brand-flush above).
      const finalSlides = slidesOverride ?? editableSlides;

      // When structure is available we go straight to /render (no second Gemini call).
      // Without it we call the legacy /carousel endpoint (Gemini + render in one shot).
      const payload = structure
        ? { templateId: tplId, slides: { templateId: tplId, themeId, slides: finalSlides }, colorTheme: themeId }
        : {
            distilledInsight: distillResult?.distilledInsight,
            hook:             distillResult?.hook,
            topic:            distillResult?.topic,
            angle:            activeAngle,
            colorTheme:       defaultTheme,
            template:         tplId,
            editedCaption:    linkedinPost.trim() || undefined,
          };
      const endpoint = structure ? `${API_URL}/api/carousel/render` : `${API_URL}/api/carousel`;
      // Boundary log — if the picker→render path ever sends slot-mismatched slides
      // the backend guard rail will 400 with a clear error AND this log will show
      // exactly what the frontend sent, so the bad transition is one console copy/paste away.
      if (structure) {
        const sentSlides = (payload as any).slides?.slides ?? [];
        console.log(
          `[handleGenerateCarousel] POST /render templateId=${tplId} themeId=${themeId} ` +
          `slideCount=${sentSlides.length} slotTypes=[${sentSlides.map((s: any) => s?.slotType ?? '?').join(',')}]`,
        );
      }
      const res  = await axios.post(endpoint, payload, axiosCfg());
      const data = res.data;

      const resolvedThemePdfs   = data.allThemePdfs ?? {};
      const resolvedColorTheme  = data.colorTheme ?? themeId;
      const resolvedPptxBase64  = data.pptxBase64 ?? data.allThemePptxs?.[themeId] ?? null;
      const resolvedRenderer    = data.renderer ?? "html";
      const resolvedUsageId     = data.usageId ?? null;
      const resolvedThemes      = templates.find(t => t.id === tplId)?.approvedThemes ?? [];

      setAllThemePdfs(resolvedThemePdfs);
      setActiveTheme(resolvedColorTheme);
      setCarouselPptxBase64(resolvedPptxBase64);
      setCarouselRenderer(resolvedRenderer);
      setCarouselUsageId(resolvedUsageId);
      setApprovedThemes(resolvedThemes);

      // Reset D1 history for this fresh generation
      setEditHistory([]);
      setHistoryIndex(-1);
      setPendingRender(false);
      setViewMode("grid");

      // D2 — seed the first tab entry (pdfDoc: null; will be set by the parse useEffect)
      const firstTab: GeneratedTemplate = {
        templateId:         tplId,
        templateName:       templates.find(t => t.id === tplId)?.name ?? tplId,
        editableSlides:     finalSlides,
        editHistory:        [],
        historyIndex:       -1,
        allThemePdfs:       resolvedThemePdfs,
        activeTheme:        resolvedColorTheme,
        approvedThemes:     resolvedThemes,
        carouselStructure:  structure,
        slideCountMin:      structure?.slideCountMin ?? 1,
        slideCountMax:      structure?.slideCountMax ?? 10,
        carouselPptxBase64: resolvedPptxBase64,
        carouselRenderer:   resolvedRenderer,
        carouselUsageId:    resolvedUsageId,
        pdfDoc:             null,
        thumbnailUrls:      [],
        currentSlide:       0,
        totalSlides:        0,
        status:             "ready",
      };
      setGeneratedTemplates([firstTab]);
      setActiveTabId(tplId);
      activeTabIdRef.current = tplId;
    } catch (e: any) {
      console.error("[carousel]", e);
      if (handleAuthExpiry(e)) { setIsCarouselLoading(false); return; }
      setCarouselError(true);
      setIsCarouselLoading(false);
    }
  };

  // ── D1 Handlers — carousel edit controls ─────────────────────────────────

  /**
   * Snapshot the current editableSlides into the undo history.
   * Drops any redo states ahead of the current index.
   * Called synchronously from event handlers (not async contexts).
   */
  const snapshotHistory = () => {
    const snapshot = JSON.parse(JSON.stringify(editableSlides)) as ContentSlide[];
    const base = editHistory.slice(0, historyIndex + 1);
    const next = [...base, snapshot];
    if (next.length > HISTORY_MAX_DEPTH) next.shift();
    setEditHistory(next);
    setHistoryIndex(next.length - 1);
  };

  /**
   * D1.1 — Regenerate: call /api/carousel/structure with the current slides +
   * an optional new slide count and/or instruction. Uses the revise prompt on
   * the backend (because existingSlides is sent).
   *
   * Two-entry history push: snapshots PRE-regen state via snapshotHistory(),
   * then appends the POST-regen state with functional updaters so redo can
   * reach the new structure after the user undoes.
   */
  const handleRegenerate = async () => {
    if (!distillResult || !selectedTemplateId || !carouselStructure) return;
    snapshotHistory(); // capture pre-regen state and drop any pending redo
    setIsRestructuring(true);
    const tpl          = templates.find(t => t.id === selectedTemplateId);
    const defaultTheme = tpl?.approvedThemes?.[0]?.key ?? carouselStructure.themeId;
    try {
      const res = await axios.post(`${API_URL}/api/carousel/structure`, {
        distilledInsight: distillResult.distilledInsight,
        hook:             distillResult.hook,
        topic:            distillResult.topic,
        angle:            activeAngle,
        colorTheme:       defaultTheme,
        template:         selectedTemplateId,
        editedCaption:    linkedinPost.trim() || undefined,
        existingSlides:   editableSlides, // signals backend to use the revise prompt
        targetSlideCount: selectedSlideCount ?? undefined,
        userInstruction:  regenInstruction.trim() || undefined,
      }, axiosCfg());
      const structure   = res.data as CarouselStructure;
      const slidesCopy  = JSON.parse(JSON.stringify(structure.slides)) as ContentSlide[];
      setCarouselStructure(structure);
      setEditableSlides(slidesCopy);
      // Push post-regen state to history via functional updaters so it chains
      // correctly on top of the pre-regen snapshot above (React batches both updates).
      setEditHistory(prev => {
        const next = [...prev, slidesCopy];
        while (next.length > HISTORY_MAX_DEPTH) next.shift();
        return next;
      });
      setHistoryIndex(prev => Math.min(prev + 1, HISTORY_MAX_DEPTH - 1));
      setSelectedSlideCount(slidesCopy.length);
      setCurrentSlide(prev => Math.min(prev, slidesCopy.length - 1));
      setRegenInstruction("");
      // Auto-render immediately — no separate "Generate Carousel" click needed.
      // Pass fresh slidesCopy directly so we don't read stale editableSlides state.
      await handleRender(slidesCopy);
    } catch (e) {
      console.error("[regen]", e);
    } finally {
      setIsRestructuring(false);
    }
  };

  /**
   * POST /api/carousel/render with the given slides (or current editableSlides).
   * Clears pendingRender and triggers the summoning animation.
   */
  const handleRender = async (slides?: ContentSlide[]) => {
    if (!selectedTemplateId || !carouselStructure) return;
    setIsRendering(true);
    setCarouselPdfDoc(null);        // clear PDF so the summoning animation fills the space
    setCarouselThumbnailUrls([]);   // thumbnails are stale; PdfStrip will re-parse when ready
    setPendingRender(false); // any render satisfies a pending state
    const themeId     = carouselStructure.themeId;
    const finalSlides = withBrandValues(slides ?? editableSlides, brandValues);
    try {
      console.log(
        `[handleRender] POST /render templateId=${selectedTemplateId} themeId=${themeId} ` +
        `slideCount=${finalSlides.length} slotTypes=[${finalSlides.map(s => s?.slotType ?? '?').join(',')}]`,
      );
      const res  = await axios.post(`${API_URL}/api/carousel/render`, {
        templateId: selectedTemplateId,
        slides:     { templateId: selectedTemplateId, themeId, slides: finalSlides },
        colorTheme: themeId,
      }, axiosCfg());
      const data = res.data;
      setAllThemePdfs(data.allThemePdfs ?? {});
      setActiveTheme(data.colorTheme ?? themeId);
      // Refresh PPTX bytes + renderer flag too — re-rendering an edited PPTX template
      // produces a NEW PPTX file, and the user's "↓ PPTX" download must reflect those edits.
      // Without this, the download stays stuck on the original generation's bytes (or null,
      // if PPTX state was wiped by an earlier broken path or by the post-OAuth state restore
      // that strips PPTX bytes from sessionStorage).
      const renderedPptx     = data.pptxBase64 ?? data.allThemePptxs?.[themeId] ?? null;
      const renderedRenderer = data.renderer ?? "html";
      setCarouselPptxBase64(renderedPptx);
      setCarouselRenderer(renderedRenderer);
      // Keep the active tab's cached state in sync too — switchTab reads from this cache,
      // so a stale cache would re-introduce the same bug after the next tab switch.
      if (activeTabIdRef.current) {
        setGeneratedTemplates(prev => prev.map(t =>
          t.templateId === activeTabIdRef.current
            ? { ...t, carouselPptxBase64: renderedPptx, carouselRenderer: renderedRenderer, allThemePdfs: data.allThemePdfs ?? t.allThemePdfs }
            : t
        ));
      }
    } catch (e: any) {
      // Surface the failure — without this, the user sees a blank carousel
      // (no spinner, no error). Previously this caused the post-OAuth-restore
      // "tabs visible, body blank" symptom whenever the render call 4xx'd.
      console.error("[render]", e);
      if (handleAuthExpiry(e)) { setIsRendering(false); setIsCarouselLoading(false); return; }
      setIsRendering(false);
      setIsCarouselLoading(false);
      setCarouselError(true);
    }
  };

  /**
   * POST /api/carousel/retheme — apply a brand colour to the current slides.
   * Uses the retheme endpoint (no Gemini, pure PPTX theming).
   * Follows the same mutation sequence as handleRender but does NOT touch
   * carouselStructure / editableSlides / selectedTemplateId (read-only).
   *
   * RI-006: setCarouselPdfDoc(null) keeps awaitingPdfParse bridge alive.
   * RI-007: clears stale PDF BEFORE the await so summoning fires immediately.
   * RI-009: catch block always resets isRendering — never swallows silently.
   */
  const handleRetheme = async (brandHex: string, cacheKey: string = 'custom') => {
    if (!selectedTemplateId || !carouselStructure) return;
    if (!/^#[0-9a-fA-F]{6}$/i.test(brandHex)) return;

    setIsRendering(true);
    setCarouselPdfDoc(null);        // clear stale PDF — awaitingPdfParse bridge keeps animation alive
    setCarouselThumbnailUrls([]);   // force PdfStrip grid to re-parse thumbnails with new PDF
    // ↑ DO NOT touch carouselStructure / editableSlides / selectedTemplateId

    const finalSlides = withBrandValues(editableSlides, brandValues);
    try {
      const res = await axios.post(`${API_URL}/api/carousel/retheme`, {
        templateId: selectedTemplateId,
        brandHex,
        content: { slides: finalSlides },
      }, axiosCfg());
      const data = res.data;
      // Merge themed PDF into the existing map — other theme PDFs stay cached
      setAllThemePdfs(prev => ({ ...prev, [cacheKey]: data.pdfBase64 }));
      setActiveTheme(cacheKey);
      setCarouselPptxBase64(data.pptxBase64 ?? null);
      // isRendering=false is handled by the PDF parse useEffect when it parses allThemePdfs[cacheKey]
    } catch (e: any) {
      console.error('[retheme]', e);
      if (handleAuthExpiry(e)) { setIsRendering(false); return; }
      setIsRendering(false);   // RI-009: reset so UI doesn't hang on error
    }
  };

  /** PPTX preset swatch click → retheme with the preset accent, cached under theme key. */
  const handlePickPresetTheme = (theme: CarouselThemeMeta) => {
    setCustomBrandColor('');
    handleRetheme(theme.accent, theme.key);
  };

  /**
   * Clear custom colour → revert to base theme from initial render (no API call needed;
   * the base-theme PDF is already in allThemePdfs[carouselStructure.themeId]).
   */
  const handleClearCustomColor = () => {
    setCustomBrandColor('');
    const baseKey = carouselStructure?.themeId ?? '';
    if (baseKey && allThemePdfs[baseKey]) {
      setCarouselPdfDoc(null);
      setCarouselThumbnailUrls([]);
      setActiveTheme(baseKey);
      setCarouselPptxBase64(null);  // base render has no themed PPTX bytes
    }
  };

  /** D1.3 — Remove slide at index and immediately re-render. */
  const handleRemoveSlide = (index: number) => {
    if (editableSlides.length <= slideCountMin) return;
    snapshotHistory();
    const newSlides = editableSlides.filter((_, i) => i !== index);
    setEditableSlides(newSlides);
    if (currentSlide >= newSlides.length) setCurrentSlide(Math.max(0, newSlides.length - 1));
    handleRender(newSlides);
  };

  // ── Post-restore auto-render ──────────────────────────────────────────────
  // When state is restored after OAuth sign-in, allThemePdfs is empty (PDFs were
  // stripped from the snapshot to fit within sessionStorage limits). This effect
  // fires once editableSlides is populated and triggers a fresh render so the
  // carousel appears within ~2–3 s without the user having to click anything.
  useEffect(() => {
    if (!restoreNeedsRenderRef.current) return;
    if (step !== "carousel" || editableSlides.length === 0 || !selectedTemplateId) return;
    restoreNeedsRenderRef.current = false;
    handleRender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableSlides]);

  /**
   * D1.4 — "Generate Carousel" button in the edit panel.
   *
   * When pendingRender=true: restoring an undo/redo snapshot — history is already
   *   correct, do NOT snapshot (that would truncate redo).
   * When pendingRender=false: fresh manual field edit — snapshot first.
   *
   * Note: regen ("Regenerate ↺") no longer sets pendingRender — it auto-renders.
   * This button is only needed for undo/redo and manual field edits.
   */
  const handlePanelRerender = async () => {
    if (!selectedTemplateId || !carouselStructure) return;
    if (!pendingRender) snapshotHistory();
    await handleRender();
  };

  /** D1.4 — Undo: restore previous snapshot and show approve banner. */
  const handleUndo = () => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    const slides = editHistory[newIndex];
    setEditableSlides(slides);
    setSelectedSlideCount(slides.length);
    setCurrentSlide(prev => Math.min(prev, slides.length - 1));
    setPendingRender(true);
  };

  /** D1.4 — Redo: re-apply an undone snapshot and show approve banner. */
  const handleRedo = () => {
    if (historyIndex >= editHistory.length - 1) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    const slides = editHistory[newIndex];
    setEditableSlides(slides);
    setSelectedSlideCount(slides.length);
    setCurrentSlide(prev => Math.min(prev, slides.length - 1));
    setPendingRender(true);
  };

  const handleGridSlideClick = useCallback((i: number) => {
    setCurrentSlide(i);
    setViewMode("single");
  }, []);

  // ── D2 — Tab management ───────────────────────────────────────────────────

  /**
   * Switch to a different template tab instantly.
   * 1. Snapshot the outgoing tab's current working state (including the cached pdfDoc).
   * 2. Set skipNextPdfParseRef so the PDF-parse useEffect skips re-parsing.
   * 3. Restore the incoming tab's snapshot into flat state — the canvas-render
   *    useEffect fires automatically and redraws from the cached pdfjs doc (~16ms).
   */
  const switchTab = useCallback((newTabId: string) => {
    const current = activeTabIdRef.current;
    if (current === newTabId) return;

    // Snapshot outgoing tab (capture live pdfDoc + cached thumbnails + slide position)
    setGeneratedTemplates(prev => prev.map(t =>
      t.templateId === current
        ? {
            ...t,
            editableSlides, editHistory, historyIndex,
            allThemePdfs, activeTheme,
            pdfDoc: carouselPdfDoc,
            thumbnailUrls: carouselThumbnailUrls,
            currentSlide, totalSlides,
          }
        : t
    ));

    // Find the incoming tab's snapshot
    const target = generatedTemplates.find(t => t.templateId === newTabId);
    if (!target) return;

    if (target.status === "ready") {
      // Only skip PDF parsing when we have a cached pdfjs doc to restore.
      // If pdfDoc is null (e.g. first visit before the parse useEffect ran),
      // let the parse useEffect run so the carousel renders normally.
      if (target.pdfDoc !== null) {
        skipNextPdfParseRef.current = true;
      }
      setEditableSlides(target.editableSlides);
      setEditHistory(target.editHistory);
      setHistoryIndex(target.historyIndex);
      setAllThemePdfs(target.allThemePdfs);
      setActiveTheme(target.activeTheme);
      setApprovedThemes(target.approvedThemes);
      setCarouselStructure(target.carouselStructure);
      setCarouselPptxBase64(target.carouselPptxBase64);
      setCarouselRenderer(target.carouselRenderer);
      setCarouselUsageId(target.carouselUsageId);
      setSelectedSlideCount(target.editableSlides.length || null);
      setCurrentSlide(target.currentSlide);
      setTotalSlides(target.totalSlides);
      setCarouselPdfDoc(target.pdfDoc);              // triggers canvas-render useEffect (fast)
      setCarouselThumbnailUrls(target.thumbnailUrls); // PdfStrip skips re-parse if non-empty
      setIsCarouselLoading(false);
      setCarouselError(false);
      setPendingRender(false);
    } else if (target.status === "loading") {
      setIsCarouselLoading(true);
      setCarouselError(false);
      setAllThemePdfs({});
      setCarouselPdfDoc(null);
      setCarouselThumbnailUrls([]);
    } else {
      setIsCarouselLoading(false);
      setCarouselError(true);
      setCarouselPdfDoc(null);
      setCarouselThumbnailUrls([]);
    }

    setActiveTabId(newTabId);
    activeTabIdRef.current = newTabId;
    // Mirror the new active tab into selectedTemplateId — see handleAddTemplate
    // for why these MUST stay in sync (post-OAuth restore + handleRender
    // template-id source of truth).
    setSelectedTemplateId(newTabId);
  }, [
    editableSlides, editHistory, historyIndex,
    allThemePdfs, activeTheme, carouselPdfDoc, carouselThumbnailUrls, currentSlide, totalSlides,
    generatedTemplates,
  ]);

  /**
   * D2 — Add a new template tab. Fires structure + render in the background.
   * If the user switches away before generation completes, the result is written
   * to the cache only (activeTabIdRef check prevents overwriting the live view).
   */
  const handleAddTemplate = useCallback(async (templateId: string) => {
    const tplMeta = templates.find(t => t.id === templateId);
    if (!tplMeta || !distillResult) return;

    const defaultTheme = tplMeta.approvedThemes[0]?.key ?? "colorshift-default";

    // Snapshot outgoing tab (including thumbnail cache)
    setGeneratedTemplates(prev => [
      ...prev.map(t =>
        t.templateId === activeTabIdRef.current
          ? {
              ...t,
              editableSlides, editHistory, historyIndex,
              allThemePdfs, activeTheme,
              pdfDoc: carouselPdfDoc,
              thumbnailUrls: carouselThumbnailUrls,
              currentSlide, totalSlides,
            }
          : t
      ),
      // New tab starts as loading
      {
        templateId,
        templateName:       tplMeta.name,
        editableSlides:     [],
        editHistory:        [],
        historyIndex:       -1,
        allThemePdfs:       {},
        activeTheme:        "",
        approvedThemes:     tplMeta.approvedThemes,
        carouselStructure:  null,
        slideCountMin:      1,
        slideCountMax:      10,
        carouselPptxBase64: null,
        carouselRenderer:   "html" as const,
        carouselUsageId:    null,
        pdfDoc:             null,
        thumbnailUrls:      [],
        currentSlide:       0,
        totalSlides:        0,
        status:             "loading" as const,
      },
    ]);

    // Switch to the new tab (shows summoning animation)
    setActiveTabId(templateId);
    activeTabIdRef.current = templateId;
    // Keep selectedTemplateId in sync with the active tab. handleRender + the
    // post-restore auto-render effect both read selectedTemplateId; if it
    // drifts away from activeTabId (e.g. user added a 2nd tab without this
    // sync), an OAuth round-trip restore will hit the backend's
    // slides_template_mismatch guard with the active tab's slides under the
    // stale template id → 400 → blank screen.
    setSelectedTemplateId(templateId);
    setIsCarouselLoading(true);
    setCarouselError(false);
    setAllThemePdfs({});
    setCarouselPdfDoc(null);
    setCarouselThumbnailUrls([]);  // new tab has no thumbnails yet
    setPickerSheetOpen(false);

    try {
      // Structure call (uses revise prompt when existingSlides provided — here we don't,
      // so Gemini generates fresh content for the chosen template)
      const structRes = await axios.post(`${API_URL}/api/carousel/structure`, structureBody(templateId, defaultTheme), axiosCfg());

      const structure   = structRes.data as CarouselStructure;
      const slidesCopy  = JSON.parse(JSON.stringify(structure.slides)) as ContentSlide[];
      const themeId     = structure.themeId ?? defaultTheme;

      // Render call
      console.log(
        `[handleAddTemplate] POST /render templateId=${templateId} themeId=${themeId} ` +
        `slideCount=${slidesCopy.length} slotTypes=[${slidesCopy.map((s: any) => s?.slotType ?? '?').join(',')}]`,
      );
      const renderRes = await axios.post(`${API_URL}/api/carousel/render`, {
        templateId,
        slides:     { templateId, themeId, slides: slidesCopy },
        colorTheme: themeId,
      }, axiosCfg());

      const data = renderRes.data;

      // /api/carousel/render returns PPTX bytes under data.allThemePptxs[themeId],
      // NOT under a top-level data.pptxBase64. Read both for backward compat.
      const tabPptxBase64 = data.pptxBase64 ?? data.allThemePptxs?.[themeId] ?? null;
      const tabRenderer   = data.renderer ?? "html";

      const readyTab: GeneratedTemplate = {
        templateId,
        templateName:       tplMeta.name,
        editableSlides:     slidesCopy,
        editHistory:        [],
        historyIndex:       -1,
        allThemePdfs:       data.allThemePdfs ?? {},
        activeTheme:        data.colorTheme ?? themeId,
        approvedThemes:     tplMeta.approvedThemes,
        carouselStructure:  structure,
        slideCountMin:      structure.slideCountMin ?? 1,
        slideCountMax:      structure.slideCountMax ?? 10,
        carouselPptxBase64: tabPptxBase64,
        carouselRenderer:   tabRenderer,
        carouselUsageId:    data.usageId ?? null,
        pdfDoc:             null,   // set by PDF-parse useEffect after flat-state restore
        thumbnailUrls:      [],     // set by PdfStrip onThumbsReady after grid renders
        currentSlide:       0,
        totalSlides:        0,
        status:             "ready",
      };

      // Always update the cache so the tab shows ✓ even if the user switched away
      setGeneratedTemplates(prev => prev.map(t =>
        t.templateId === templateId ? readyTab : t
      ));

      // Only update flat working state if this tab is still active
      if (activeTabIdRef.current === templateId) {
        setEditableSlides(slidesCopy);
        setEditHistory([]);
        setHistoryIndex(-1);
        setAllThemePdfs(data.allThemePdfs ?? {});
        setActiveTheme(data.colorTheme ?? themeId);
        setApprovedThemes(tplMeta.approvedThemes);
        setCarouselStructure(structure);
        setCarouselPptxBase64(tabPptxBase64);
        setCarouselRenderer(tabRenderer);
        setCarouselUsageId(data.usageId ?? null);
        setSelectedSlideCount(slidesCopy.length);
        setIsCarouselLoading(false);
        setPendingRender(false);
        setViewMode("grid");
        setBrandFields(structure.brandFields ?? []);
        const initVals: Record<string, string> = {};
        for (const f of (structure.brandFields ?? [])) initVals[f.key] = f.currentValue;
        setBrandValues(initVals);
        setBrandOriginals(initVals);
      }
    } catch (e) {
      console.error("[addTemplate]", e);
      setGeneratedTemplates(prev => prev.map(t =>
        t.templateId === templateId
          ? { ...t, status: "error", errorMessage: "Generation failed" }
          : t
      ));
      if (activeTabIdRef.current === templateId) {
        setIsCarouselLoading(false);
        setCarouselError(true);
      }
    }
  }, [
    templates, distillResult, activeAngle, linkedinPost,
    editableSlides, editHistory, historyIndex,
    allThemePdfs, activeTheme, carouselPdfDoc, carouselThumbnailUrls, currentSlide, totalSlides,
    preferredSlideCount,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * D2 — Retry generation for the currently active tab that is in "error" state.
   * Re-runs /api/carousel/structure + /api/carousel/render in-place (same tab entry),
   * without creating a new tab or switching away.
   */
  const handleRetryTemplate = useCallback(async () => {
    const failedTabId = activeTabIdRef.current;
    const failedTab   = generatedTemplates.find(t => t.templateId === failedTabId);
    if (!failedTab || failedTab.status !== "error" || !distillResult) return;

    const tplMeta      = templates.find(t => t.id === failedTabId);
    if (!tplMeta) return;
    const defaultTheme = tplMeta.approvedThemes[0]?.key ?? "colorshift-default";

    // Reset this tab to loading in-place
    setGeneratedTemplates(prev => prev.map(t =>
      t.templateId === failedTabId ? { ...t, status: "loading", errorMessage: undefined } : t
    ));
    setIsCarouselLoading(true);
    setCarouselError(false);
    setAllThemePdfs({});
    setCarouselPdfDoc(null);
    setCarouselThumbnailUrls([]);

    try {
      const structRes   = await axios.post(`${API_URL}/api/carousel/structure`, structureBody(failedTabId, defaultTheme), axiosCfg());

      const structure  = structRes.data as CarouselStructure;
      const slidesCopy = JSON.parse(JSON.stringify(structure.slides)) as ContentSlide[];
      const themeId    = structure.themeId ?? defaultTheme;

      console.log(
        `[handleRetryTemplate] POST /render templateId=${failedTabId} themeId=${themeId} ` +
        `slideCount=${slidesCopy.length} slotTypes=[${slidesCopy.map((s: any) => s?.slotType ?? '?').join(',')}]`,
      );
      const renderRes  = await axios.post(`${API_URL}/api/carousel/render`, {
        templateId: failedTabId,
        slides:     { templateId: failedTabId, themeId, slides: slidesCopy },
        colorTheme: themeId,
      }, axiosCfg());
      const data = renderRes.data;

      // Same field-shape as handleAddTemplate / handleGenerateCarousel:
      // /api/carousel/render returns PPTX bytes under allThemePptxs[themeId].
      const retryPptxBase64 = data.pptxBase64 ?? data.allThemePptxs?.[themeId] ?? null;
      const retryRenderer   = data.renderer ?? "html";

      const readyTab: GeneratedTemplate = {
        templateId:         failedTabId,
        templateName:       tplMeta.name,
        editableSlides:     slidesCopy,
        editHistory:        [],
        historyIndex:       -1,
        allThemePdfs:       data.allThemePdfs ?? {},
        activeTheme:        data.colorTheme ?? themeId,
        approvedThemes:     tplMeta.approvedThemes,
        carouselStructure:  structure,
        slideCountMin:      structure.slideCountMin ?? 1,
        slideCountMax:      structure.slideCountMax ?? 10,
        carouselPptxBase64: retryPptxBase64,
        carouselRenderer:   retryRenderer,
        carouselUsageId:    data.usageId ?? null,
        pdfDoc:             null,
        thumbnailUrls:      [],
        currentSlide:       0,
        totalSlides:        0,
        status:             "ready",
      };

      setGeneratedTemplates(prev => prev.map(t =>
        t.templateId === failedTabId ? readyTab : t
      ));

      // Only update flat working state if the user hasn't switched away
      if (activeTabIdRef.current === failedTabId) {
        setEditableSlides(slidesCopy);
        setEditHistory([]);
        setHistoryIndex(-1);
        setAllThemePdfs(data.allThemePdfs ?? {});
        setActiveTheme(data.colorTheme ?? themeId);
        setApprovedThemes(tplMeta.approvedThemes);
        setCarouselStructure(structure);
        setCarouselPptxBase64(retryPptxBase64);
        setCarouselRenderer(retryRenderer);
        setCarouselUsageId(data.usageId ?? null);
        setSelectedSlideCount(slidesCopy.length);
        setIsCarouselLoading(false);
        setPendingRender(false);
        setViewMode("grid");
        setBrandFields(structure.brandFields ?? []);
        const initVals: Record<string, string> = {};
        for (const f of (structure.brandFields ?? [])) initVals[f.key] = f.currentValue;
        setBrandValues(initVals);
        setBrandOriginals(initVals);
      }
    } catch (e) {
      console.error("[retryTemplate]", e);
      setGeneratedTemplates(prev => prev.map(t =>
        t.templateId === failedTabId
          ? { ...t, status: "error", errorMessage: "Generation failed" }
          : t
      ));
      if (activeTabIdRef.current === failedTabId) {
        setIsCarouselLoading(false);
        setCarouselError(true);
      }
    }
  }, [generatedTemplates, templates, distillResult, activeAngle, linkedinPost, preferredSlideCount]);
  // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * D2 — Close a template tab.
   * Cannot close the last remaining tab.
   * If closing the active tab, switches to the adjacent one first so the
   * snapshot/restore path in switchTab runs cleanly before removal.
   */
  const closeTab = useCallback((templateId: string) => {
    if (generatedTemplates.length <= 1) return;
    const idx     = generatedTemplates.findIndex(t => t.templateId === templateId);
    const isActive = templateId === activeTabIdRef.current;

    if (isActive) {
      // Pick the tab to switch to: previous if possible, else next
      const adjacentIdx = idx > 0 ? idx - 1 : idx + 1;
      const adjacentId  = generatedTemplates[adjacentIdx].templateId;
      switchTab(adjacentId);
    }
    // Remove the closed tab from the array
    setGeneratedTemplates(prev => prev.filter(t => t.templateId !== templateId));
  }, [generatedTemplates, switchTab]);

  // ── Auth actions ──────────────────────────────────────────────────────────

  /**
   * Snapshot all serializable V2 state to sessionStorage before navigating away
   * to Google OAuth. Called via onClick on every "Continue with Google" link so
   * that state is fully restored when LoginSuccess redirects back to /distill-v2.
   *
   * pdfDoc (non-serializable pdfjs object) and thumbnailUrls (derived) are excluded;
   * they are re-derived from the restored base64 PDFs on the next mount.
   */
  /**
   * Snapshot all serializable V2 state to sessionStorage before navigating to Google OAuth.
   * Large base64 PDFs are intentionally OMITTED to stay within sessionStorage's ~5 MB limit.
   * On restore, `needsRender: true` signals that handleRender() should be called automatically
   * to regenerate the carousel PDF (~2–3 s loading) before the user can download.
   */
  const saveV2State = () => {
    try {
      sessionStorage.setItem("distill_v2_state", JSON.stringify({
        ts: Date.now(),
        needsRender: step === "carousel" && editableSlides.length > 0,
        step, thought, distillResult, activeAngle, generatedPosts,
        linkedinPost, isEditing, preferredSlideCount,
        selectedTemplateId, activeCategory,
        carouselStructure, editableSlides,
        brandFields, brandValues, brandOriginals,
        activeTheme, approvedThemes,
        currentSlide, totalSlides,
        carouselRenderer, carouselUsageId,
        selectedSlideCount, regenInstruction,
        viewMode, panelOpen, editHistory, historyIndex,
        liPrepared, liFullText,
        // D2 tabs: metadata only — allThemePdfs and pdfDoc stripped (too large)
        generatedTemplates: generatedTemplates.map(t => ({
          ...t, allThemePdfs: {}, pdfDoc: null, thumbnailUrls: [], carouselPptxBase64: null,
        })),
        activeTabId,
        // allThemePdfs / carouselPptxBase64 intentionally omitted (PDF base64 ~1–9 MB)
      }));
    } catch { /* sessionStorage full — user starts fresh after sign-in; acceptable fallback */ }
  };

  /**
   * Call this inside any API catch block when a 401 is received.
   * Saves all current state (so it survives the OAuth redirect) and opens
   * the sign-in modal. Returns true if it was a session-expiry 401 so the
   * caller knows NOT to also show a generic error UI.
   */
  const handleAuthExpiry = (e: any): boolean => {
    if (e?.response?.status !== 401) return false;
    saveV2State();
    openAuthModal("session");
    return true;
  };

  const handleLogout = () => {
    axios.get(`${API_URL}/auth/logout`, { withCredentials: true })
      .finally(() => {
        localStorage.removeItem("distill_auth_token");
        // Also clear the guest token — it may be expired (issued before or during the
        // authenticated session). Without this, the next template pick would send the
        // stale token → 401. We immediately re-issue a fresh guest JWT below.
        localStorage.removeItem("distill_guest_token");
        isAuthenticatedRef.current = false;   // keep interceptor ref in sync
        setIsAuthenticated(false);
        setAuthUserName("");
        setLoggedOutToast(true);
        setTimeout(() => setLoggedOutToast(false), 3000);
        // Re-issue a fresh guest JWT so the user can generate carousels immediately
        // after logging out without waiting for a page reload.
        const id = localStorage.getItem("distill_install_id") ?? "";
        guestTokenReadyRef.current = axios.post(`${API_URL}/auth/guest-login`, { install_id: id })
          .then(r => {
            if (r.data?.jwt) {
              localStorage.setItem("distill_guest_token", r.data.jwt);
              console.log("[logout] fresh guest-login OK");
            } else {
              console.error("[logout] guest-login returned no jwt — response keys:", Object.keys(r.data ?? {}));
            }
          })
          .catch(e => {
            console.error("[logout] guest-login FAILED:", e?.response?.status, e?.response?.data, e?.message);
          });
      });
  };

  // ── Download handlers ─────────────────────────────────────────────────────

  const handleDownloadPdf = () => {
    const b64 = allThemePdfs[activeTheme];
    if (!b64) return;
    triggerDownload(
      Uint8Array.from(atob(b64), c => c.charCodeAt(0)),
      "carousel.pdf",
      "application/pdf",
    );
    if (carouselUsageId) {
      axios.patch(`${API_URL}/api/carousel/download`, { usageId: carouselUsageId }, axiosCfg()).catch(() => {});
    }
  };

  const handleDownloadPptx = () => {
    if (!carouselPptxBase64) return;
    triggerDownload(
      Uint8Array.from(atob(carouselPptxBase64), c => c.charCodeAt(0)),
      "carousel.pptx",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    );
    if (carouselUsageId) {
      axios.patch(`${API_URL}/api/carousel/download`, { usageId: carouselUsageId }, axiosCfg()).catch(() => {});
    }
  };

  // ── LinkedIn posting handlers ─────────────────────────────────────────────

  const handlePrepareLinkedIn = () => {
    const tags     = (distillResult?.toneTags ?? []).map(t => `#${t}`).join(" ");
    const fullText = tags ? `${linkedinPost}\n\n${tags}` : linkedinPost;
    setLiFullText(fullText);
    setLiPreviewOpen(true);
    // For admin: fetch current LinkedIn connect status each time the modal opens
    if (isAdminUser) {
      axios.get(`${API_URL}/api/user/linkedin-status`, axiosCfg())
        .then((r: any) => setLiDirectConnected(!!r.data?.connected))
        .catch(() => setLiDirectConnected(false));
    }
  };

  // Opens the LinkedIn OAuth popup and waits for the postMessage callback
  const handleLiDirectConnect = () => {
    const jwt = localStorage.getItem("distill_auth_token");
    if (!jwt) return;
    const popup = window.open(
      `${API_URL}/auth/linkedin/connect?jwt=${encodeURIComponent(jwt)}`,
      "linkedin-connect",
      "popup,width=620,height=700,left=200,top=100",
    );
    if (!popup) return;
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === "linkedin_connected") {
        window.removeEventListener("message", onMessage);
        setLiDirectConnected(true);
        setLiDirectError(null);
      } else if (e.data?.type === "linkedin_error") {
        window.removeEventListener("message", onMessage);
        setLiDirectError(e.data.error ?? "Failed to connect LinkedIn");
      }
    };
    window.addEventListener("message", onMessage);
  };

  // Renders the carousel server-side and publishes it directly to LinkedIn
  const handleLiDirectPublish = async () => {
    if (!selectedTemplateId || editableSlides.length === 0) return;
    setLiDirectPublishing(true);
    setLiDirectError(null);
    try {
      const tags     = (distillResult?.toneTags ?? []).map(t => `#${t}`).join(" ");
      const caption  = tags ? `${linkedinPost}\n\n${tags}` : linkedinPost;
      const res = await axios.post(
        `${API_URL}/api/carousel/publish-to-linkedin`,
        {
          captionText: caption,
          templateId:  selectedTemplateId,
          colorTheme:  activeTheme,
          slides:      editableSlides,
        },
        axiosCfg(),
      );
      const liShareUrl: string = res.data?.shareUrl ?? "https://www.linkedin.com/feed/";
      setLiPreviewOpen(false);
      setLiDirectPostUrl(liShareUrl);

      // Draft is now published — clean it up
      deleteDraft();

      // Track publish count (drives the post-publish sign-in gate, same as copy-paste flow)
      const newCount = parseInt(localStorage.getItem("distill_publish_count") ?? "0", 10) + 1;
      localStorage.setItem("distill_publish_count", String(newCount));

      // Create a distill share link so the overlay can show "share your post" alongside the
      // live LinkedIn URL. Two-step (POST then PATCH) to avoid blocking on a large PDF body —
      // mirrors the copy-paste flow in handleLiConfirm.
      if (distillResult?.distilledInsight) {
        const pdfB64 = allThemePdfs[activeTheme] ?? '';
        axios.post(`${API_URL}/api/share`, {
          distilledInsight: distillResult.distilledInsight,
          topic:      distillResult.topic,
          toneTags:   distillResult.toneTags,
          hook:       distillResult.hook,
          pov:        distillResult.pov,
          outputs:    { linkedin: linkedinPost, totalSlides: String(totalSlides), authorName: authUserName || '' },
          angle:      activeAngle,
          install_id: localStorage.getItem('distill_install_id') ?? undefined,
        }, axiosCfg())
          .then((r: any) => {
            setLiShareToken(r.data.token);
            if (pdfB64) {
              axios.patch(`${API_URL}/api/share/${r.data.token}/pdf`, { pdf: pdfB64 }, axiosCfg())
                .catch(() => {});
            }
          })
          .catch(() => {});
      }

      setLiPasteOverlay(true);
    } catch (err: any) {
      const code = err?.response?.data?.error;
      if (code === "linkedin_not_connected" || code === "linkedin_token_expired") {
        setLiDirectConnected(false);
        setLiDirectError("LinkedIn session expired — please reconnect.");
      } else {
        setLiDirectError(err?.response?.data?.detail ?? "Failed to publish. Please try again.");
      }
    } finally {
      setLiDirectPublishing(false);
    }
  };

  const handleLiConfirm = () => {
    setLiPreviewOpen(false);
    setLiLaunching(true);
    setLiPrepared(true);

    // ── CLIPBOARD: must fire synchronously while document still has focus ─
    // navigator.clipboard.writeText requires the document to be focused.
    // window.open (below) shifts focus to the new tab, so clipboard MUST be
    // initiated first. No `await` — we don't yield, so the Promise resolves
    // in the background while we continue synchronously.
    navigator.clipboard.writeText(liFullText).catch(() => {});

    // Track publish count immediately (drives the post-publish sign-in gate)
    const newCount = parseInt(localStorage.getItem("distill_publish_count") ?? "0", 10) + 1;
    localStorage.setItem("distill_publish_count", String(newCount));

    // Draft is now published — clean it up
    deleteDraft();

    // ── DELAY window.open + download by ~1 s ─────────────────────────────
    // Problem: window.open fires synchronously and the LinkedIn tab steals
    // focus before React can even paint the launching animation — user never
    // sees it. Fix: defer to a setTimeout so the animation renders first.
    //
    // Clipboard was already initiated synchronously above (focus constraint
    // satisfied). Chrome's user-gesture window for popups is ~1–2 s, so
    // 1 100 ms is safely inside it on desktop. On mobile, if the popup is
    // blocked the location.href fallback fires instead — acceptable trade-off
    // for an animation that would be invisible on mobile anyway.
    //
    // ⚠ Still DO NOT pass "noopener"/"noreferrer" in the features string —
    // that forces window.open to return null and triggers the fallback on
    // every desktop click. Null the opener manually on the returned Window.
    setTimeout(() => {
      const LINKEDIN_URL = "https://www.linkedin.com/feed/?shareActive=true";
      const liWin = window.open(LINKEDIN_URL, "_blank");
      if (liWin) {
        try { liWin.opener = null; } catch { /* cross-origin — ignore */ }
      } else {
        console.warn("[handleLiConfirm] window.open returned null — falling back to location change.");
        window.location.href = LINKEDIN_URL;
      }

      const b64 = allThemePdfs[activeTheme];
      if (b64) {
        const slug = (distillResult?.topic ?? "carousel")
          .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
        triggerDownload(
          Uint8Array.from(atob(b64), c => c.charCodeAt(0)),
          `${slug}-carousel.pdf`,
          "application/octet-stream",
        );
        if (carouselUsageId) {
          axios.patch(`${API_URL}/api/carousel/download`, { usageId: carouselUsageId }, axiosCfg()).catch(() => {});
        }
      }

      // Create the shareable post link (fire-and-forget, two-step).
      // Step 1: POST without the PDF — small body (~5 KB), returns token fast.
      // Step 2: PATCH with the PDF only — can be 10–20 MB for Puppeteer templates;
      //         kept separate so a large PDF never blocks step 1 from resolving.
      if (distillResult?.distilledInsight) {
        const pdfB64 = allThemePdfs[activeTheme] ?? '';
        axios.post(`${API_URL}/api/share`, {
          distilledInsight: distillResult.distilledInsight,
          topic:      distillResult.topic,
          toneTags:   distillResult.toneTags,
          hook:       distillResult.hook,
          pov:        distillResult.pov,
          outputs:    { linkedin: liFullText, totalSlides: String(totalSlides), authorName: authUserName || '' },
          angle:      activeAngle,
          install_id: localStorage.getItem('distill_install_id') ?? undefined,
        }, axiosCfg())
          .then((r: any) => {
            setLiShareToken(r.data.token);
            // Step 2: upload PDF in background so the carousel shows on the share page
            if (pdfB64) {
              axios.patch(`${API_URL}/api/share/${r.data.token}/pdf`, { pdf: pdfB64 }, axiosCfg())
                .catch(() => {});
            }
          })
          .catch(() => {});
      }

      setLiLaunching(false);
      setLiPasteOverlay(true);
    }, 4400);
  };

  /** Reset all V2 state back to Step 1 for a new thought. */
  const handleNewThought = () => {
    deleteDraft(); // discard the in-progress draft — user is starting fresh
    setStep("input");
    setThought("");
    setDistillResult(null);
    setLinkedinPost("");
    setGeneratedPosts({});
    setSelectedTemplateId(null);
    setCarouselPdfDoc(null);
    setAllThemePdfs({});
    setLiPrepared(false);
    setLiPreviewOpen(false);
    setLiLaunching(false);
    setLiShareToken(null);
    setPanelOpen(false);
    setEditHistory([]);
    setHistoryIndex(-1);
    setPreferredSlideCount(null);
    // D2 — clear all tabs
    setGeneratedTemplates([]);
    setActiveTabId("");
    activeTabIdRef.current = "";
    setPickerSheetOpen(false);
    setCarouselThumbnailUrls([]);
  };

  /**
   * Wrapper around setActiveTheme for user-initiated theme switches.
   * Clears carouselThumbnailUrls before switching so PdfStrip's preRenderedUrls
   * short-circuit doesn't fire with the previous theme's stale thumbnails.
   * Without this, grid view stays frozen on the old theme while single view
   * updates correctly (single view is driven by carouselPdfDoc, not the thumb cache).
   */
  const handleSetActiveTheme = useCallback((theme: string) => {
    setCarouselThumbnailUrls([]);
    setActiveTheme(theme);
  }, []);

  /**
   * D2 — Called by PdfStrip when it finishes parsing a PDF into thumbnail data-URLs.
   * Stores them in state (so the active tab's PdfStrip can use them on the next render)
   * and also writes them into the active tab's cache entry (so switchTab can restore
   * them instantly for future return visits).
   */
  const handleThumbsReady = useCallback((urls: string[]) => {
    setCarouselThumbnailUrls(urls);
    setGeneratedTemplates(prev => prev.map(t =>
      t.templateId === activeTabIdRef.current
        ? { ...t, thumbnailUrls: urls }
        : t
    ));
  }, []); // stable — reads activeTabIdRef.current via ref, not closure

  // ── Render ────────────────────────────────────────────────────────────────

  // While a ?draft= is being fetched and state is being set, show a neutral
  // loading screen so the user never sees the "raw thought" input page flash.
  if (isDraftLoading) {
    return (
      <div style={{ ...pageStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            border: '3px solid #E4E4E0', borderTopColor: '#5B4FE8',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 14px',
          }} />
          <div style={{ fontSize: 14, color: '#888', fontWeight: 600 }}>Resuming your draft…</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Global styles for toolbar buttons, pills, panel inputs, and panel animations */}
      <style>{PANEL_CSS}</style>

      {/* Main page — shifts left when the desktop panel is open */}
      <div style={{
        ...pageStyle,
        paddingRight: step === "carousel" && panelOpen && !isMobile ? 388 : 0,
        transition:   "padding-right 0.3s cubic-bezier(.22,1,.36,1)",
      }}>
        {/* ── Sticky nav ── */}
        <nav style={navStyle}>
          {/* Logo */}
          <div style={{
            display: "flex", alignItems: "center", gap: "8px",
            fontSize: "18px", fontWeight: 800, letterSpacing: "-0.04em",
          }}>
            <img src="/favicon.svg" alt="" width="20" height="20" style={{ display: "block", flexShrink: 0 }} />
            distill
            <sup style={{ fontSize: "8px", letterSpacing: "0.1em", fontWeight: 600, color: "#5B4FE8", verticalAlign: "super", marginLeft: "2px", textTransform: "uppercase" }}>
              v2
            </sup>
          </div>

          {/* Auth / user area */}
          {isAuthenticated ? (
            <div className="nav-user">
              <div className="nav-avatar">
                {authUserName ? authUserName.charAt(0).toUpperCase() : "U"}
              </div>
              <span className="nav-user-name">{authUserName || "Signed in"}</span>
              {isAdminUser && (
                <>
                  <a
                    href="/admin/templates"
                    style={{ color: "#A89FFF", fontSize: "11px", fontWeight: 600, textDecoration: "none", marginRight: "2px", letterSpacing: "0.03em" }}
                    title="Admin: manage templates"
                  >
                    Admin ↗
                  </a>
                  <a
                    href="/app"
                    style={{ color: "#7ec8a0", fontSize: "11px", fontWeight: 600, textDecoration: "none", marginRight: "2px", letterSpacing: "0.03em" }}
                    title="Switch to V1 app"
                  >
                    v1 ↗
                  </a>
                  <a
                    href="/dashboard"
                    style={{ color: "#A89FFF", fontSize: "11px", fontWeight: 600, textDecoration: "none", marginRight: "2px", letterSpacing: "0.03em" }}
                    title="Your Dashboard — all campaigns at a glance"
                  >
                    Dashboard ↗
                  </a>
                  <a
                    href="/campaign-planner"
                    style={{ color: "#F5A623", fontSize: "11px", fontWeight: 600, textDecoration: "none", marginRight: "2px", letterSpacing: "0.03em" }}
                    title="Campaign Planner"
                  >
                    Campaigns ↗
                  </a>
                </>
              )}
              <button className="nav-logout-btn" onClick={handleLogout} title="Sign out">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0"/>
                  <line x1="12" y1="2" x2="12" y2="12"/>
                </svg>
              </button>
            </div>
          ) : (
            <button className="nav-pill" onClick={() => openAuthModal("nav")}>Sign in</button>
          )}
        </nav>

        {/* Logout toast */}
        {loggedOutToast && (
          <div className="logout-toast">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            You've been signed out
          </div>
        )}

        {/* ── Step router ── */}
        <div style={mainStyle}>
          {step === "input" && (
            <StepInput
              thought={thought}
              isDistilling={isDistilling}
              distillError={distillError}
              preferredSlideCount={preferredSlideCount}
              setThought={v => { setThought(v); if (distillError) setDistillError(null); }}
              setPreferredSlideCount={setPreferredSlideCount}
              onDistill={handleDistill}
            />
          )}

          {step === "insight" && distillResult && (
            <StepInsight
              thought={thought}
              distillResult={distillResult}
              activeAngle={activeAngle}
              linkedinPost={linkedinPost}
              isEditing={isEditing}
              isGeneratingPost={isGeneratingPost}
              isDistilling={isDistilling}
              preferredSlideCount={preferredSlideCount}
              setLinkedinPost={handleEditLinkedinPost}
              setIsEditing={setIsEditing}
              setPreferredSlideCount={setPreferredSlideCount}
              onAngle={handleAngle}
              onNext={() => setStep("template")}
              onReDistill={handleDistill}
              onRevise={handleRevise}
              isRevising={isRevising}
              revisionHistory={revisionInstructions[activeAngle] ?? []}
              onRemoveRevisionInstruction={handleRemoveRevisionInstruction}
              perspective={perspective}
              onTogglePerspective={handleTogglePerspective}
            />
          )}

          {step === "template" && (
            <StepTemplatePicker
              templates={templates}
              templatesLoaded={templatesLoaded}
              templatesError={templatesError}
              pickError={pickError}
              onDismissPickError={() => setPickError(null)}
              selectedTemplateId={selectedTemplateId}
              activeCategory={activeCategory}
              isStructureLoading={isStructureLoading}
              preferredSlideCount={preferredSlideCount}
              setActiveCategory={setActiveCategory}
              onPickTemplate={handlePickTemplate}
              onRetry={handleRetryTemplates}
              onBack={() => setStep("insight")}
              onChangePreferredSlideCount={setPreferredSlideCount}
            />
          )}

          {step === "content" && (
            <StepContent
              brandFields={brandFields}
              brandValues={brandValues}
              brandOriginals={brandOriginals}
              isSavingBrand={isSavingBrand}
              brandSaveError={brandSaveError}
              brandSaved={brandSaved}
              setBrandValues={setBrandValues}
              setBrandSaved={setBrandSaved}
              onSaveBrand={() => handleSaveBrandData(false)}
              onGenerate={() => handleGenerateCarousel()}
              onBack={() => setStep("template")}
            />
          )}

          {step === "carousel" && (
            <StepCarousel
              isCarouselLoading={isCarouselLoading}
              isRendering={isRendering}
              isRestructuring={isRestructuring}
              carouselError={carouselError}
              craftingMessage={craftingMessage}
              carouselPdfDoc={carouselPdfDoc}
              allThemePdfs={allThemePdfs}
              activeTheme={activeTheme}
              approvedThemes={approvedThemes}
              currentSlide={currentSlide}
              totalSlides={totalSlides}
              viewMode={viewMode}
              panelOpen={panelOpen}
              isMobile={isMobile}
              slideCountMin={slideCountMin}
              carouselRenderer={carouselRenderer}
              carouselPptxBase64={carouselPptxBase64}
              carouselCanvasRef={carouselCanvasRef}
              setCurrentSlide={setCurrentSlide}
              setViewMode={setViewMode}
              setActiveTheme={handleSetActiveTheme}
              setPanelOpen={setPanelOpen}
              onDownloadPdf={handleDownloadPdf}
              onDownloadPptx={handleDownloadPptx}
              onPrepareLinkedIn={handlePrepareLinkedIn}
              onChangeTemplate={() => {
                // Sync the slide-count preference to the active tab's current count
                // before opening the picker. If the user regenerated at 6 slides but
                // the Step-1 preference was 8, the picker and the next structure call
                // should both use 6 — not the stale 8.
                // Guard: only sync if the count is within the active template's stated
                // range — prevents updating to a non-representative value when the
                // carousel returned fewer slides than the template's minimum.
                const n = editableSlides.length;
                if (n >= slideCountMin && n <= slideCountMax) setPreferredSlideCount(n);
                setPickerSheetOpen(true);
              }}
              onNewThought={handleNewThought}
              onRemoveSlide={handleRemoveSlide}
              onGridSlideClick={handleGridSlideClick}
              onRetryTemplate={handleRetryTemplate}
              preRenderedThumbnails={carouselThumbnailUrls}
              onThumbsReady={handleThumbsReady}
              customBrandColor={customBrandColor}
              onCustomBrandColorChange={setCustomBrandColor}
              onApplyCustomColor={() => handleRetheme(customBrandColor, 'custom')}
              onPickPresetTheme={handlePickPresetTheme}
              onClearCustomColor={handleClearCustomColor}
              tabBar={generatedTemplates.length > 0 ? (
                /* D2 — Template tab bar: rendered by StepCarousel right above the card */
                <div style={{
                  display:        "flex",
                  alignItems:     "center",
                  gap:            "4px",
                  overflowX:      "auto",
                  scrollbarWidth: "none",
                }}>
                  {generatedTemplates.map(tab => {
                    const isActive  = tab.templateId === activeTabId;
                    const isLast    = generatedTemplates.length === 1;
                    const shortName = tab.templateName.length > 20
                      ? tab.templateName.slice(0, 19) + "…"
                      : tab.templateName;
                    return (
                      <div
                        key={tab.templateId}
                        style={{
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "5px",
                          padding:      "6px 10px 6px 12px",
                          borderRadius: "8px",
                          border:       "1.5px solid",
                          borderColor:  isActive ? "#5B4FE8" : "#E4E4E0",
                          background:   isActive ? "rgba(91,79,232,0.06)" : "#fff",
                          cursor:       tab.status === "loading" ? "default" : "pointer",
                          whiteSpace:   "nowrap",
                          flexShrink:   0,
                          fontFamily:   "'Bricolage Grotesque', sans-serif",
                          transition:   "all 0.15s",
                          boxShadow:    isActive ? "0 0 0 2px rgba(91,79,232,0.18)" : "none",
                        }}
                        onClick={() => tab.status !== "loading" && switchTab(tab.templateId)}
                      >
                        {tab.status === "loading" && (
                          <span style={{ fontSize: "11px", animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span>
                        )}
                        {tab.status === "ready" && <span style={{ fontSize: "11px", color: "#27ae60" }}>✓</span>}
                        {tab.status === "error"  && (
                          <span
                            title="Click to retry"
                            style={{ fontSize: "11px", color: "#e74c3c", cursor: "pointer" }}
                            onClick={e => { e.stopPropagation(); handleRetryTemplate(); }}
                          >!</span>
                        )}
                        <span style={{
                          fontSize:   "12px",
                          fontWeight: isActive ? 700 : 500,
                          color:      isActive ? "#5B4FE8" : "#555",
                        }}>
                          {shortName}
                        </span>
                        {!isLast && (
                          <button
                            aria-label={`Close ${tab.templateName}`}
                            onClick={e => { e.stopPropagation(); closeTab(tab.templateId); }}
                            style={{
                              background: "none", border: "none", cursor: "pointer",
                              padding: "2px 4px", minWidth: "20px", minHeight: "20px",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              color: "#aaa", fontSize: "13px", borderRadius: "4px",
                              lineHeight: 1, marginLeft: "2px",
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = "#e74c3c"; (e.currentTarget as HTMLButtonElement).style.background = "rgba(231,76,60,0.08)"; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = "#aaa"; (e.currentTarget as HTMLButtonElement).style.background = "none"; }}
                          >✕</button>
                        )}
                      </div>
                    );
                  })}
                  {generatedTemplates.length < MAX_TEMPLATE_TABS && (
                    <button
                      onClick={() => {
                        // Sync slide-count preference to the active tab's real count before
                        // opening the picker — prevents stale Step-1 value from filtering
                        // templates incorrectly when the user has regenerated at fewer slides.
                        // Guard: only sync when the count is within the active template's range.
                        const n = editableSlides.length;
                        if (n >= slideCountMin && n <= slideCountMax) setPreferredSlideCount(n);
                        setPickerSheetOpen(true);
                      }}
                      style={{
                        display: "flex", alignItems: "center", gap: "4px",
                        padding: "6px 12px", borderRadius: "8px",
                        border: "1.5px dashed #C0B8F8", background: "none",
                        color: "#5B4FE8", fontSize: "12px", fontWeight: 600,
                        cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                        fontFamily: "'Bricolage Grotesque', sans-serif", transition: "all 0.15s",
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(91,79,232,0.06)"; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}
                    >+ Template</button>
                  )}
                </div>
              ) : undefined}
            />
          )}
        </div>
      </div>

      {/* ── Edit panel overlays ── */}

      {/* Mobile semi-transparent backdrop — tap anywhere to close the drawer */}
      {panelOpen && isMobile && (
        <div
          onClick={() => setPanelOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 199, background: "rgba(0,0,0,0.18)" }}
        />
      )}

      {/* Edit panel itself (desktop side-panel or mobile drawer) */}
      {panelOpen && carouselStructure && !isCarouselLoading && (!isRendering || !isMobile) && step === "carousel" && (
        <EditPanel
          isOpen={panelOpen}
          isMobile={isMobile}
          carouselStructure={carouselStructure}
          editableSlides={editableSlides}
          currentSlide={currentSlide}
          selectedSlideCount={selectedSlideCount}
          regenInstruction={regenInstruction}
          isRestructuring={isRestructuring}
          pendingRender={pendingRender}
          slideCountMin={slideCountMin}
          slideCountMax={slideCountMax}
          canUndo={canUndo}
          canRedo={canRedo}
          brandFields={brandFields}
          brandValues={brandValues}
          isSavingBrand={isSavingBrand}
          brandSaved={brandSaved}
          brandSaveError={brandSaveError}
          hasPanelBrandChanges={hasPanelBrandChanges}
          panelFocusRef={panelFocusRef}
          setCurrentSlide={setCurrentSlide}
          setSelectedSlideCount={setSelectedSlideCount}
          setRegenInstruction={setRegenInstruction}
          setEditableSlides={setEditableSlides}
          setBrandValues={setBrandValues}
          setBrandSaved={setBrandSaved}
          onClose={() => setPanelOpen(false)}
          onRegenerate={handleRegenerate}
          onRerender={handlePanelRerender}
          onRemoveSlide={handleRemoveSlide}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onSaveBrand={() => handleSaveBrandData(false)}
        />
      )}

      {/* Desktop "Edit" vertical tab — visible when panel is closed */}
      {step === "carousel" && !panelOpen && !isMobile && carouselPdfDoc && !isCarouselLoading && !isRendering && (
        <button
          onClick={() => setPanelOpen(true)}
          title="Open edit panel"
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.background = "#4B3FD8";
            (e.currentTarget as HTMLButtonElement).style.boxShadow  = "-6px 0 28px rgba(91,79,232,0.38)";
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.background = "#5B4FE8";
            (e.currentTarget as HTMLButtonElement).style.boxShadow  = "-4px 0 20px rgba(91,79,232,0.28)";
          }}
          style={{
            position:       "fixed",
            right:          0,
            top:            "50%",
            transform:      "translateY(-50%)",
            zIndex:         200,
            display:        "flex",
            flexDirection:  "column",
            alignItems:     "center",
            justifyContent: "center",
            gap:            "8px",
            padding:        "20px 10px",
            background:     "#5B4FE8",
            color:          "#fff",
            border:         "none",
            borderRadius:   "12px 0 0 12px",
            cursor:         "pointer",
            fontFamily:     "'Bricolage Grotesque', sans-serif",
            boxShadow:      "-4px 0 20px rgba(91,79,232,0.28)",
            transition:     "background 0.15s, box-shadow 0.15s",
          }}
        >
          <span style={{ fontSize: "15px", lineHeight: 1 }}>✏</span>
          <span style={{
            fontSize:      "11px",
            fontWeight:    700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            writingMode:   "vertical-rl" as React.CSSProperties["writingMode"],
          }}>Edit</span>
        </button>
      )}

      {/* Mobile FAB — floating edit button (carousel step, panel closed, mobile) */}
      {step === "carousel" && !panelOpen && isMobile && !isCarouselLoading && !isRendering && carouselPdfDoc && (
        <button
          onClick={() => setPanelOpen(true)}
          aria-label="Edit slide"
          style={{
            position:       "fixed",
            right:          "20px",
            bottom:         "24px",
            zIndex:         990,
            width:          "56px",
            height:         "56px",
            borderRadius:   "50%",
            background:     "#5B4FE8",
            color:          "#fff",
            border:         "none",
            fontSize:       "20px",
            cursor:         "pointer",
            boxShadow:      "0 4px 20px rgba(91,79,232,0.4)",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
          }}
        >
          ✏
        </button>
      )}

      {/* ── D2 — Template picker sheet ── */}
      {pickerSheetOpen && (
        <div
          onClick={() => setPickerSheetOpen(false)}
          style={{
            position:       "fixed",
            inset:          0,
            zIndex:         300,
            background:     "rgba(10,10,10,0.45)",
            display:        "flex",
            alignItems:     isMobile ? "flex-end" : "center",
            justifyContent: "center",
            animation:      "sheetFadeIn 180ms ease",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background:    "#fff",
              borderRadius:  isMobile ? "20px 20px 0 0" : "16px",
              width:         isMobile ? "100%" : "min(860px, 92vw)",
              maxHeight:     isMobile ? "85vh" : "88vh",
              overflowY:     "auto",
              display:       "flex",
              flexDirection: "column",
              animation:     isMobile ? "sheetSlideUp 260ms cubic-bezier(.22,1,.36,1)" : "sheetFadeIn 200ms ease",
            }}
          >
            {/* Mobile drag handle — no extra header, StepTemplatePicker owns the content */}
            {isMobile && (
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px", flexShrink: 0 }}>
                <div style={{ width: "36px", height: "4px", background: "#E4E4E0", borderRadius: "2px" }} />
              </div>
            )}
            {/* Padding wrapper — mirrors mainStyle so StepTemplatePicker looks
                identical to how it renders on Step 3. Without this, the grid
                bleeds edge-to-edge against the modal card. */}
            <div style={{ padding: "24px 24px 32px", display: "flex", flexDirection: "column", gap: "28px" }}>
              <StepTemplatePicker
                templates={templates.filter(t => !generatedTemplates.find(g => g.templateId === t.id))}
                templatesLoaded={templatesLoaded}
                templatesError={null}
                selectedTemplateId={null}
                activeCategory={sheetCategory}
                isStructureLoading={false}
                preferredSlideCount={preferredSlideCount}
                setActiveCategory={setSheetCategory}
                onPickTemplate={handleAddTemplate}
                onRetry={() => {}}
                onBack={() => setPickerSheetOpen(false)}
                onChangePreferredSlideCount={setPreferredSlideCount}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── LinkedIn overlays ── */}
      {liPreviewOpen && (
        <LinkedInPostPreview
          postText={liFullText}
          pdfBase64={allThemePdfs[activeTheme] ?? null}
          pptxBase64={carouselPptxBase64}
          totalSlides={totalSlides}
          documentTitle={distillResult?.topic ?? undefined}
          userName={authUserName || undefined}
          onPost={handleLiConfirm}
          onClose={() => setLiPreviewOpen(false)}
          isAdmin={isAdminUser}
          linkedinConnected={liDirectConnected}
          linkedinPublishing={liDirectPublishing}
          linkedinError={liDirectError}
          onConnectLinkedIn={handleLiDirectConnect}
          onPublishLinkedIn={handleLiDirectPublish}
        />
      )}
      {liLaunching && <LinkedInLaunching />}
      {liPasteOverlay && <LinkedInPasteOverlay liFeedback={liFeedback} setLiFeedback={setLiFeedback} shareToken={liShareToken} linkedInPostUrl={liDirectPostUrl} onClose={() => {
        setLiPasteOverlay(false);
        setLiFeedback(null);
        setLiDirectPostUrl(null);
        // After aha-moment overlay closes, evaluate whether to show the sign-in prompt.
        // Only for unauthenticated users — authenticated users never see the gate.
        if (!isAuthenticatedRef.current) {
          const count = parseInt(localStorage.getItem("distill_publish_count") ?? "0", 10);
          if (count >= 2)      setSignInPromptLevel("hard");
          else if (count >= 1) setSignInPromptLevel("soft");
        }
      }} />}

      {/* ── Auth modal (sign-in gate for download / publish) ── */}
      {showAuthModal && (
        <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <button className="auth-modal-close" onClick={() => setShowAuthModal(false)}>✕</button>
            <div className="auth-modal-icon">✦</div>
            <p className="auth-modal-title">{AUTH_MODAL_COPY[authModalReason].title}</p>
            <p className="auth-modal-sub">{AUTH_MODAL_COPY[authModalReason].sub}</p>
            <a
              className="auth-google-btn"
              href={`${API_URL}/auth/google?install_id=${encodeURIComponent(installId)}`}
              onClick={saveV2State}
            >
              <svg className="auth-google-icon" viewBox="0 0 24 24" width="18" height="18">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </a>
          </div>
        </div>
      )}

      {/* ── Post-publish sign-in gate (three-tier) ──────────────────────────────
          soft (1 publish)  → dismissible card with "Maybe later"
          hard (2+ publishes) → blocking overlay, no dismiss button
          Authenticated users never see this. ─────────────────────────────── */}
      {signInPromptLevel !== "none" && !isAuthenticated && (
        <div style={{
          position:       "fixed",
          inset:          0,
          zIndex:         9998,
          background:     signInPromptLevel === "hard"
            ? "rgba(10,10,15,0.85)"
            : "rgba(10,10,15,0.65)",
          backdropFilter: "blur(8px)",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
          padding:        "24px",
          fontFamily:     "'Bricolage Grotesque', sans-serif",
          animation:      "ahaFadeIn 320ms cubic-bezier(.22,1,.36,1)",
        }}
        /* Soft mode: clicking backdrop dismisses. Hard mode: no dismiss. */
        onClick={signInPromptLevel === "soft" ? () => setSignInPromptLevel("none") : undefined}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background:    "#fff",
              borderRadius:  "20px",
              padding:       "40px 36px 32px",
              maxWidth:      "400px",
              width:         "100%",
              textAlign:     "center",
              boxShadow:     "0 32px 80px rgba(0,0,0,0.28)",
              position:      "relative",
            }}
          >
            {/* Soft-only dismiss (×) */}
            {signInPromptLevel === "soft" && (
              <button
                onClick={() => setSignInPromptLevel("none")}
                aria-label="Dismiss sign-in prompt"
                style={{
                  position:   "absolute",
                  top:        "16px",
                  right:      "16px",
                  background: "none",
                  border:     "none",
                  cursor:     "pointer",
                  color:      "#bbb",
                  fontSize:   "18px",
                  lineHeight: 1,
                  padding:    "4px 8px",
                }}
              >✕</button>
            )}

            <div style={{ fontSize: "40px", marginBottom: "16px" }}>🎉</div>

            <h2 style={{
              fontSize:     "22px",
              fontWeight:   800,
              color:        "#0A0A0A",
              marginBottom: "10px",
              letterSpacing: "-0.03em",
            }}>
              Your carousel is live!
            </h2>

            <p style={{
              fontSize:     "14px",
              color:        "#666",
              lineHeight:   1.6,
              marginBottom: "28px",
            }}>
              {signInPromptLevel === "hard"
                ? "Sign in to keep creating carousels — your work is always saved and restored."
                : "Sign in to save your work and build more carousels."}
            </p>

            <a
              href={`${API_URL}/auth/google?install_id=${encodeURIComponent(installId)}`}
              onClick={saveV2State}
              style={{
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
                gap:            "10px",
                width:          "100%",
                padding:        "13px 0",
                borderRadius:   "10px",
                background:     ACCENT,
                color:          "#fff",
                fontSize:       "15px",
                fontWeight:     700,
                textDecoration: "none",
                boxSizing:      "border-box" as React.CSSProperties["boxSizing"],
                marginBottom:   "8px",
              }}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" style={{ flexShrink: 0 }}>
                <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google →
            </a>

            {/* Only soft prompt gets a "Maybe later" escape hatch */}
            {signInPromptLevel === "soft" && (
              <button
                onClick={() => setSignInPromptLevel("none")}
                style={{
                  background:  "none",
                  border:      "none",
                  color:       "#999",
                  fontSize:    "13px",
                  cursor:      "pointer",
                  padding:     "10px",
                  width:       "100%",
                  fontFamily:  "'Bricolage Grotesque', sans-serif",
                }}
              >
                Maybe later
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Session-expired overlay ── */}
      {sessionExpired && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(15,15,35,0.88)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "24px",
        }}>
          <div style={{
            background: "#fff", borderRadius: "20px",
            padding: "40px 36px", maxWidth: "380px", width: "100%",
            textAlign: "center", boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
          }}>
            <div style={{ fontSize: "40px", marginBottom: "16px" }}>🔒</div>
            <h2 style={{ fontSize: "20px", fontWeight: 700, color: "#111", marginBottom: "8px" }}>
              Session expired
            </h2>
            <p style={{ fontSize: "14px", color: "#666", lineHeight: "1.6", marginBottom: "28px" }}>
              Your login session has timed out. Sign in again to continue — your work is saved in this tab.
            </p>
            <a
              href={`${API_URL}/auth/google?install_id=${encodeURIComponent(installId)}`}
              onClick={saveV2State}
              style={{
                display: "block", width: "100%",
                padding: "13px 0", borderRadius: "10px",
                background: ACCENT, color: "#fff",
                fontSize: "15px", fontWeight: 700,
                textDecoration: "none", boxSizing: "border-box",
              }}
            >
              Sign in with Google →
            </a>
          </div>
        </div>
      )}
    </>
  );
};

// ── Utility ───────────────────────────────────────────────────────────────────

/** Create a temporary <a> to trigger a file download from a byte array. */
function triggerDownload(bytes: Uint8Array, filename: string, mimeType: string) {
  // Cast required: TS strict mode treats Uint8Array<ArrayBufferLike> as incompatible
  // with BlobPart's ArrayBufferView<ArrayBuffer> due to SharedArrayBuffer ambiguity.
  const blob = new Blob([bytes as unknown as BlobPart], { type: mimeType });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── LinkedIn launching animation ──────────────────────────────────────────────

const LinkedInLaunching: React.FC = () => (
  <div style={{
    position: "fixed", inset: 0, zIndex: 2100,
    background: "rgba(8,8,8,0.88)", backdropFilter: "blur(14px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: "'Bricolage Grotesque', sans-serif",
  }}>
    <style>{`
      @keyframes liCheckPop {
        0%   { transform: scale(0.4); opacity: 0; }
        70%  { transform: scale(1.12); opacity: 1; }
        100% { transform: scale(1);   opacity: 1; }
      }
      @keyframes liLaunchUp {
        from { opacity: 0; transform: translateY(20px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .li-check-ring  { animation: liCheckPop  500ms cubic-bezier(.34,1.56,.64,1) both; }
      .li-launch-h    { animation: liLaunchUp  460ms cubic-bezier(.22,1,.36,1) 180ms both; }
      .li-launch-sub  { animation: liLaunchUp  460ms cubic-bezier(.22,1,.36,1) 340ms both; }
      @media (prefers-reduced-motion: reduce) {
        .li-check-ring, .li-launch-h, .li-launch-sub { animation: none; }
      }
    `}</style>
    <div style={{ textAlign: "center", padding: "0 40px" }}>
      {/* Animated check circle */}
      <div className="li-check-ring" style={{
        width: "80px", height: "80px", borderRadius: "50%",
        background: "rgba(74,222,128,0.1)",
        border: "2px solid rgba(74,222,128,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 28px",
      }}>
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
          <path d="M5 13l4 4L19 7" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      {/* Headline */}
      <p className="li-launch-h" style={{
        margin: "0 0 12px",
        fontSize: "32px", fontWeight: 800, color: "#fff",
        letterSpacing: "-0.03em", lineHeight: 1.1,
      }}>
        Caption copied
      </p>
      {/* Sub-line with LinkedIn logo */}
      <div className="li-launch-sub" style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
        color: "rgba(255,255,255,0.5)", fontSize: "17px", fontWeight: 500,
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <rect width="24" height="24" rx="4" fill="#0A66C2"/>
          <path d="M7 10H5v8h2v-8zM6 9a1.25 1.25 0 100-2.5A1.25 1.25 0 006 9zM19 14c0-2.2-1.5-4-3.5-4a3.5 3.5 0 00-2.5 1.1V10H11v8h2v-4c0-1.1.9-2 2-2s2 .9 2 2v4h2v-4z" fill="#fff"/>
        </svg>
        <span>Opening LinkedIn…</span>
      </div>
    </div>
  </div>
);

/**
 * "Aha moment" overlay shown immediately after the user clicks "Let's go".
 * Confirms the three actions completed and invites LinkedIn feedback.
 */
const LinkedInPasteOverlay: React.FC<{
  liFeedback:    "up" | "down" | null;
  setLiFeedback: (v: "up" | "down") => void;
  shareToken:    string | null;
  /** When non-null, the overlay renders in "published" mode — copy + bottom CTA change
   *  to acknowledge the post is already live on LinkedIn. */
  linkedInPostUrl?: string | null;
  onClose:       () => void;
}> = ({ liFeedback, setLiFeedback, shareToken, linkedInPostUrl = null, onClose }) => {
  const [linkCopied, setLinkCopied] = React.useState(false);
  const shareUrl = shareToken ? `${window.location.origin}/share/${shareToken}` : null;
  const isPublished = !!linkedInPostUrl;

  const copyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).catch(() => {});
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
  <div
    style={{
      position: "fixed", inset: 0, zIndex: 2000,
      background: "rgba(10,10,10,0.62)", backdropFilter: "blur(10px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px", fontFamily: "'Bricolage Grotesque', sans-serif",
      animation: "ahaFadeIn 360ms cubic-bezier(.22,1,.36,1)",
    }}
    onClick={onClose}
  >
    <style>{AHA_CSS}</style>
    <div
      className="aha-card"
      style={{
        background: "#FAFAF9", borderRadius: "20px",
        maxWidth: "440px", width: "100%",
        boxShadow: "0 32px 80px rgba(0,0,0,0.24)", overflow: "hidden",
      }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ padding: "44px 36px 32px", textAlign: "center" }}>
        <div className="aha-rule" style={{ height: "2px", background: isPublished ? "#16A34A" : "#5B4FE8", margin: "0 auto 22px", borderRadius: "2px" }} />
        <p className="aha-eyebrow" style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: isPublished ? "#16A34A" : "#5B4FE8", margin: "0 0 18px" }}>
          {isPublished ? "✓ Published to LinkedIn" : "Your post is ready"}
        </p>
        <p className="aha-quote" style={{ fontFamily: "'Bricolage Grotesque', Georgia, serif", fontStyle: "italic", fontSize: "24px", lineHeight: 1.32, color: "#0A0A0A", margin: "0 0 12px", letterSpacing: "-0.02em", fontWeight: 500 }}>
          {isPublished
            ? <>&ldquo;You showed up.&rdquo;</>
            : <>&ldquo;An idea you keep to yourself helps no one.&rdquo;</>}
        </p>
        <p className="aha-sub" style={{ fontSize: "13px", color: "#888", margin: 0, letterSpacing: "0.01em" }}>
          {isPublished ? "Your post is live on LinkedIn." : "You shipped one today."}
        </p>
      </div>

      <div className="aha-conf" style={{ padding: "18px 36px", borderTop: "1px solid #EBEBE7", borderBottom: "1px solid #EBEBE7" }}>
        {(isPublished
          ? ["Posted to your LinkedIn feed", "Carousel embedded as document"]
          : ["Caption copied to clipboard", "PDF saved to Downloads"]
        ).map(label => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
            <span style={{ width: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", color: isPublished ? "#16A34A" : "#5B4FE8", flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            <span style={{ fontSize: "14px", color: "#0A0A0A", fontWeight: 500 }}>{label}</span>
          </div>
        ))}
      </div>

      {/* ── Share link ── */}
      <div style={{ padding: "16px 36px", borderTop: "1px solid #EBEBE7" }}>
        <p style={{ margin: "0 0 10px", fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#5B4FE8" }}>
          Share your post
        </p>
        {shareUrl ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{
              flex: 1, padding: "8px 12px",
              background: "#F4F4F2", borderRadius: "8px",
              fontSize: "12px", color: "#555", fontWeight: 500,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {shareUrl}
            </div>
            <button
              onClick={copyLink}
              style={{
                flexShrink: 0, padding: "8px 14px",
                background: linkCopied ? "#f0fdf4" : "#0A0A0A",
                color: linkCopied ? "#16a34a" : "#fff",
                border: linkCopied ? "1px solid #86efac" : "none",
                borderRadius: "8px", fontSize: "12px", fontWeight: 700,
                cursor: "pointer", fontFamily: "'Bricolage Grotesque', sans-serif",
                transition: "all 0.2s", whiteSpace: "nowrap",
              }}
            >
              {linkCopied ? "✓ Copied" : "Copy link"}
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "16px", height: "16px", border: "2px solid #ddd", borderTopColor: "#5B4FE8", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <span style={{ fontSize: "12px", color: "#aaa" }}>Generating link…</span>
          </div>
        )}
        <p style={{ margin: "8px 0 0", fontSize: "11px", color: "#bbb", lineHeight: 1.4 }}>
          Others can read your post and create their own
        </p>
      </div>

      <div className="aha-fb" style={{ padding: "20px 36px 4px", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
        {liFeedback ? (
          <p style={{ fontSize: "13px", color: "#5B4FE8", fontWeight: 600, margin: 0 }}>Thanks — noted.</p>
        ) : (
          <>
            <p style={{ fontSize: "12px", color: "#999", fontWeight: 500, margin: 0, letterSpacing: "0.02em" }}>Did this carousel hit the mark?</p>
            <div style={{ display: "flex", gap: "10px" }}>
              {(["up", "down"] as const).map(dir => (
                <button
                  key={dir}
                  className="aha-thumb"
                  aria-label={`Thumbs ${dir}`}
                  onClick={() => setLiFeedback(dir)}
                  style={{ width: "40px", height: "40px", padding: 0, background: "#fff", border: "1.5px solid #E4E4E0", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#666" }}
                >
                  {dir === "up" ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H7a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L14 1a3.13 3.13 0 0 1 3 3.88Z"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H17a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L10 23a3.13 3.13 0 0 1-3-3.88Z"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="aha-cta" style={{ padding: "18px 36px 32px" }}>
        <button
          style={{ width: "100%", padding: "14px", background: "#0A66C2", color: "#fff", border: "none", borderRadius: "12px", fontSize: "15px", fontWeight: 700, letterSpacing: "-0.01em", cursor: "pointer", fontFamily: "'Bricolage Grotesque', sans-serif" }}
          onClick={() => {
            if (isPublished && linkedInPostUrl) {
              const win = window.open(linkedInPostUrl, "_blank");
              if (win) { try { win.opener = null; } catch { /* cross-origin */ } }
            }
            onClose();
          }}
        >
          {isPublished ? "View on LinkedIn ↗" : "Post on LinkedIn ↗"}
        </button>
        <p style={{ fontSize: "12px", color: "#aaa", textAlign: "center", margin: "10px 0 0", letterSpacing: "0.01em" }}>
          {isPublished ? "Opens your post in a new tab" : "LinkedIn is open in another tab"}
        </p>
      </div>
    </div>
  </div>
  );
};

// ── CSS strings ───────────────────────────────────────────────────────────────

/** CSS for toolbar buttons, pills, panel inputs, panel animations, and D2 tab spinner. */
const PANEL_CSS = `
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes sheetSlideUp {
    from { transform: translateY(100%); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
  @keyframes sheetFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slideUpPanel {
    from { transform: translateY(100%); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
  @keyframes slideInRight {
    from { transform: translateX(100%); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }
  .d1-toolbar-btn {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 5px 10px; border-radius: 8px; border: 1.5px solid #E4E4E0;
    background: #fff; color: #444; font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.15s; white-space: nowrap;
    font-family: 'Bricolage Grotesque', sans-serif;
  }
  .d1-toolbar-btn:hover { border-color: #5B4FE8; color: #5B4FE8; }
  .d1-toolbar-btn.active { background: rgba(91,79,232,0.08); border-color: #5B4FE8; color: #5B4FE8; }
  .d1-toolbar-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .d1-toolbar-btn:disabled:hover { border-color: #E4E4E0; color: #444; }
  .d1-pill {
    padding: 5px 12px; border-radius: 100px; border: 1.5px solid #E4E4E0;
    background: #fff; color: #666; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: all 0.15s;
    font-family: 'Bricolage Grotesque', sans-serif;
  }
  .d1-pill.active { background: rgba(91,79,232,0.08); border-color: #5B4FE8; color: #5B4FE8; }
  .d1-panel-input {
    width: 100%; box-sizing: border-box;
    border: 1.5px solid #E4E4E0; border-radius: 10px;
    padding: 9px 14px; outline: none;
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 14px; background: #fff; color: #0A0A0A;
    transition: border-color 0.15s;
  }
  .d1-panel-input:focus { border-color: #5B4FE8; }
  .d1-panel-textarea {
    width: 100%; box-sizing: border-box;
    border: 1.5px solid #E4E4E0; border-radius: 10px;
    padding: 9px 14px; outline: none; resize: none;
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 14px; background: #fff; color: #0A0A0A;
    transition: border-color 0.15s; line-height: 1.5; min-height: 72px;
  }
  .d1-panel-textarea:focus { border-color: #5B4FE8; }
`;

/** CSS for the aha-moment overlay animations. */
const AHA_CSS = `
  @keyframes ahaFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes ahaCardIn {
    from { opacity: 0; transform: scale(.97) translateY(16px); }
    to   { opacity: 1; transform: scale(1)   translateY(0); }
  }
  @keyframes ahaRise {
    from { opacity: 0; transform: translateY(7px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes ahaRule { from { width: 0; opacity: 0; } to { width: 32px; opacity: 1; } }
  .aha-card    { animation: ahaCardIn 600ms cubic-bezier(.22,1,.36,1) both; }
  .aha-rule    { animation: ahaRule 700ms cubic-bezier(.22,1,.36,1) 240ms both; }
  .aha-eyebrow { animation: ahaRise 500ms cubic-bezier(.22,1,.36,1) 320ms both; }
  .aha-quote   { animation: ahaRise 700ms cubic-bezier(.22,1,.36,1) 420ms both; }
  .aha-sub     { animation: ahaRise 500ms cubic-bezier(.22,1,.36,1) 580ms both; }
  .aha-conf    { animation: ahaRise 500ms cubic-bezier(.22,1,.36,1) 720ms both; }
  .aha-fb      { animation: ahaRise 500ms cubic-bezier(.22,1,.36,1) 860ms both; }
  .aha-cta     { animation: ahaRise 500ms cubic-bezier(.22,1,.36,1) 980ms both; }
  .aha-thumb   { transition: transform 220ms cubic-bezier(.22,1,.36,1), background 180ms, border-color 180ms, color 180ms; }
  .aha-thumb:hover { transform: translateY(-2px) scale(1.06); border-color: #5B4FE8; color: #5B4FE8; }
  @media (prefers-reduced-motion: reduce) {
    .aha-card, .aha-rule, .aha-eyebrow, .aha-quote, .aha-sub, .aha-conf, .aha-fb, .aha-cta { animation: none; }
  }
`;

export default DistillPageV2;
